/**
 * Generated by orval 🍺
 * Do not edit manually.
 * TrainLoop Platform V2 API
 * Model evaluation workbench API
 * OpenAPI spec version: 0.0.0
 */
import { useMutation, useQuery } from "@tanstack/react-query";
import type {
  DataTag,
  DefinedInitialDataOptions,
  DefinedUseQueryResult,
  MutationFunction,
  QueryClient,
  QueryFunction,
  QueryKey,
  UndefinedInitialDataOptions,
  UseMutationOptions,
  UseMutationResult,
  UseQueryOptions,
  UseQueryResult,
} from "@tanstack/react-query";

import type {
  CheckpointsResponse,
  DeleteModelApiModelsModelIdDelete200,
  FallbackOptionsResponse,
  GatewayModelInfo,
  GetModelEngineMetricsApiModelsModelIdEngineMetricsGetParams,
  GetModelMetricsApiModelsModelIdMetricsGetParams,
  GetModelScalingApiModelsModelIdScalingGetParams,
  GetModelScalingChangesApiModelsModelIdScalingChangesGetParams,
  GetModelScalingUsageApiModelsModelIdScalingUsageGetParams,
  GetModelUptimeApiModelsModelIdUptimeGetParams,
  GetModelUsageDailyApiModelsModelIdUsageDailyGetParams,
  GetModelUsageSummaryApiModelsModelIdUsageGetParams,
  GetModelUsageTimeseriesApiModelsModelIdUsageTimeseriesGetParams,
  HTTPValidationError,
  LatencyAlertsUpdate,
  ListModelRequestsApiModelsModelIdRequestsGetParams,
  ModelActivityResponse,
  ModelAliasCreate,
  ModelCreate,
  ModelEngineMetricsResponse,
  ModelMetricsResponse,
  ModelResponse,
  ModelScalingResponse,
  ModelScalingUpdate,
  ModelScalingUsageResponse,
  ModelSchedulesResponse,
  ModelSchedulesUpdate,
  ModelUpdate,
  ReplicaSettingChangeResponse,
  RequestLogResponse,
  TraceLoggingModelUpdate,
  TraceLoggingState,
  UptimeResponse,
  UsageDailyResponse,
  UsageSummaryResponse,
  UsageTimeseriesResponse,
} from "../model";

import { orvalFetch } from "../../client.ts";
import type { ErrorType } from "../../client.ts";

type SecondParameter<T extends (...args: never) => unknown> = Parameters<T>[1];

const withQueryKey = <T extends object, K>(
  query: T,
  queryKey: K,
): T & { queryKey: K } => {
  const result = { queryKey } as T & { queryKey: K };
  for (const key of Object.keys(query)) {
    // The explicit queryKey always wins, matching the previous
    // `{ ...query, queryKey }` spread where it was set last.
    if (key === "queryKey") continue;
    Object.defineProperty(result, key, {
      enumerable: true,
      configurable: true,
      get: () => (query as Record<string, unknown>)[key],
    });
  }
  return result;
};

export type listAvailableModelsApiModelsAvailableGetResponse200 = {
  data: GatewayModelInfo[];
  status: 200;
};

export type listAvailableModelsApiModelsAvailableGetResponse422 = {
  data: HTTPValidationError;
  status: 422;
};

export type listAvailableModelsApiModelsAvailableGetResponseSuccess =
  listAvailableModelsApiModelsAvailableGetResponse200 & {
    headers: Headers;
  };
export type listAvailableModelsApiModelsAvailableGetResponseError =
  listAvailableModelsApiModelsAvailableGetResponse422 & {
    headers: Headers;
  };

export type listAvailableModelsApiModelsAvailableGetResponse =
  | listAvailableModelsApiModelsAvailableGetResponseSuccess
  | listAvailableModelsApiModelsAvailableGetResponseError;

export const getListAvailableModelsApiModelsAvailableGetUrl = () => {
  return `/api/models/available`;
};

/**
 * List the gateway models this team is allowed to use.
 *
 * Wraps `GET /v1/models` on the gateway with the team's gateway API key
 * — response shape is OpenAI-standard (`{object: 'list', data: [...]}`).
 * Used by the create-model dropdown.
 *
 * Normally provisions `team_credentials` lazily (the dropdown is often the
 * first thing a new team touches). Under staff impersonation that lazy
 * provisioning would be a WRITE on a read-only request — and would create
 * real gateway state for a team the staff member doesn't belong to — so we
 * only READ existing creds and return an empty list if the team has none.
 * @summary List Available Models
 */
export const listAvailableModelsApiModelsAvailableGet = async (
  options?: Parameters<typeof orvalFetch>[1],
): Promise<listAvailableModelsApiModelsAvailableGetResponse> => {
  return orvalFetch<listAvailableModelsApiModelsAvailableGetResponse>(
    getListAvailableModelsApiModelsAvailableGetUrl(),
    {
      ...options,
      method: "GET",
    },
  );
};

export const getListAvailableModelsApiModelsAvailableGetQueryKey = () => {
  return [`/api/models/available`] as const;
};

export const getListAvailableModelsApiModelsAvailableGetQueryOptions = <
  TData = Awaited<ReturnType<typeof listAvailableModelsApiModelsAvailableGet>>,
  TError = ErrorType<HTTPValidationError>,
>(options?: {
  query?: Partial<
    UseQueryOptions<
      Awaited<ReturnType<typeof listAvailableModelsApiModelsAvailableGet>>,
      TError,
      TData
    >
  >;
  request?: SecondParameter<typeof orvalFetch>;
}) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getListAvailableModelsApiModelsAvailableGetQueryKey();

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof listAvailableModelsApiModelsAvailableGet>>
  > = ({ signal }) =>
    listAvailableModelsApiModelsAvailableGet({ signal, ...requestOptions });

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof listAvailableModelsApiModelsAvailableGet>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type ListAvailableModelsApiModelsAvailableGetQueryResult = NonNullable<
  Awaited<ReturnType<typeof listAvailableModelsApiModelsAvailableGet>>
>;
export type ListAvailableModelsApiModelsAvailableGetQueryError =
  ErrorType<HTTPValidationError>;

export function useListAvailableModelsApiModelsAvailableGet<
  TData = Awaited<ReturnType<typeof listAvailableModelsApiModelsAvailableGet>>,
  TError = ErrorType<HTTPValidationError>,
>(
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof listAvailableModelsApiModelsAvailableGet>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listAvailableModelsApiModelsAvailableGet>>,
          TError,
          Awaited<ReturnType<typeof listAvailableModelsApiModelsAvailableGet>>
        >,
        "initialData"
      >;
    request?: SecondParameter<typeof orvalFetch>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useListAvailableModelsApiModelsAvailableGet<
  TData = Awaited<ReturnType<typeof listAvailableModelsApiModelsAvailableGet>>,
  TError = ErrorType<HTTPValidationError>,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof listAvailableModelsApiModelsAvailableGet>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listAvailableModelsApiModelsAvailableGet>>,
          TError,
          Awaited<ReturnType<typeof listAvailableModelsApiModelsAvailableGet>>
        >,
        "initialData"
      >;
    request?: SecondParameter<typeof orvalFetch>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useListAvailableModelsApiModelsAvailableGet<
  TData = Awaited<ReturnType<typeof listAvailableModelsApiModelsAvailableGet>>,
  TError = ErrorType<HTTPValidationError>,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof listAvailableModelsApiModelsAvailableGet>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof orvalFetch>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
/**
 * @summary List Available Models
 */

export function useListAvailableModelsApiModelsAvailableGet<
  TData = Awaited<ReturnType<typeof listAvailableModelsApiModelsAvailableGet>>,
  TError = ErrorType<HTTPValidationError>,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof listAvailableModelsApiModelsAvailableGet>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof orvalFetch>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions =
    getListAvailableModelsApiModelsAvailableGetQueryOptions(options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}

export type getTraceLoggingApiModelsTraceLoggingGetResponse200 = {
  data: TraceLoggingState;
  status: 200;
};

export type getTraceLoggingApiModelsTraceLoggingGetResponse422 = {
  data: HTTPValidationError;
  status: 422;
};

export type getTraceLoggingApiModelsTraceLoggingGetResponseSuccess =
  getTraceLoggingApiModelsTraceLoggingGetResponse200 & {
    headers: Headers;
  };
export type getTraceLoggingApiModelsTraceLoggingGetResponseError =
  getTraceLoggingApiModelsTraceLoggingGetResponse422 & {
    headers: Headers;
  };

export type getTraceLoggingApiModelsTraceLoggingGetResponse =
  | getTraceLoggingApiModelsTraceLoggingGetResponseSuccess
  | getTraceLoggingApiModelsTraceLoggingGetResponseError;

export const getGetTraceLoggingApiModelsTraceLoggingGetUrl = () => {
  return `/api/models/trace-logging`;
};

/**
 * Which models the gateway captures request/response bodies for.
 * @summary Get Trace Logging
 */
export const getTraceLoggingApiModelsTraceLoggingGet = async (
  options?: Parameters<typeof orvalFetch>[1],
): Promise<getTraceLoggingApiModelsTraceLoggingGetResponse> => {
  return orvalFetch<getTraceLoggingApiModelsTraceLoggingGetResponse>(
    getGetTraceLoggingApiModelsTraceLoggingGetUrl(),
    {
      ...options,
      method: "GET",
    },
  );
};

export const getGetTraceLoggingApiModelsTraceLoggingGetQueryKey = () => {
  return [`/api/models/trace-logging`] as const;
};

export const getGetTraceLoggingApiModelsTraceLoggingGetQueryOptions = <
  TData = Awaited<ReturnType<typeof getTraceLoggingApiModelsTraceLoggingGet>>,
  TError = ErrorType<HTTPValidationError>,
>(options?: {
  query?: Partial<
    UseQueryOptions<
      Awaited<ReturnType<typeof getTraceLoggingApiModelsTraceLoggingGet>>,
      TError,
      TData
    >
  >;
  request?: SecondParameter<typeof orvalFetch>;
}) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getGetTraceLoggingApiModelsTraceLoggingGetQueryKey();

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getTraceLoggingApiModelsTraceLoggingGet>>
  > = ({ signal }) =>
    getTraceLoggingApiModelsTraceLoggingGet({ signal, ...requestOptions });

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getTraceLoggingApiModelsTraceLoggingGet>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetTraceLoggingApiModelsTraceLoggingGetQueryResult = NonNullable<
  Awaited<ReturnType<typeof getTraceLoggingApiModelsTraceLoggingGet>>
>;
export type GetTraceLoggingApiModelsTraceLoggingGetQueryError =
  ErrorType<HTTPValidationError>;

export function useGetTraceLoggingApiModelsTraceLoggingGet<
  TData = Awaited<ReturnType<typeof getTraceLoggingApiModelsTraceLoggingGet>>,
  TError = ErrorType<HTTPValidationError>,
>(
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getTraceLoggingApiModelsTraceLoggingGet>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getTraceLoggingApiModelsTraceLoggingGet>>,
          TError,
          Awaited<ReturnType<typeof getTraceLoggingApiModelsTraceLoggingGet>>
        >,
        "initialData"
      >;
    request?: SecondParameter<typeof orvalFetch>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetTraceLoggingApiModelsTraceLoggingGet<
  TData = Awaited<ReturnType<typeof getTraceLoggingApiModelsTraceLoggingGet>>,
  TError = ErrorType<HTTPValidationError>,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getTraceLoggingApiModelsTraceLoggingGet>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getTraceLoggingApiModelsTraceLoggingGet>>,
          TError,
          Awaited<ReturnType<typeof getTraceLoggingApiModelsTraceLoggingGet>>
        >,
        "initialData"
      >;
    request?: SecondParameter<typeof orvalFetch>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetTraceLoggingApiModelsTraceLoggingGet<
  TData = Awaited<ReturnType<typeof getTraceLoggingApiModelsTraceLoggingGet>>,
  TError = ErrorType<HTTPValidationError>,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getTraceLoggingApiModelsTraceLoggingGet>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof orvalFetch>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
/**
 * @summary Get Trace Logging
 */

export function useGetTraceLoggingApiModelsTraceLoggingGet<
  TData = Awaited<ReturnType<typeof getTraceLoggingApiModelsTraceLoggingGet>>,
  TError = ErrorType<HTTPValidationError>,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getTraceLoggingApiModelsTraceLoggingGet>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof orvalFetch>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions =
    getGetTraceLoggingApiModelsTraceLoggingGetQueryOptions(options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}

export type setTraceLoggingApiModelsTraceLoggingPutResponse200 = {
  data: TraceLoggingState;
  status: 200;
};

export type setTraceLoggingApiModelsTraceLoggingPutResponse422 = {
  data: HTTPValidationError;
  status: 422;
};

export type setTraceLoggingApiModelsTraceLoggingPutResponseSuccess =
  setTraceLoggingApiModelsTraceLoggingPutResponse200 & {
    headers: Headers;
  };
export type setTraceLoggingApiModelsTraceLoggingPutResponseError =
  setTraceLoggingApiModelsTraceLoggingPutResponse422 & {
    headers: Headers;
  };

export type setTraceLoggingApiModelsTraceLoggingPutResponse =
  | setTraceLoggingApiModelsTraceLoggingPutResponseSuccess
  | setTraceLoggingApiModelsTraceLoggingPutResponseError;

export const getSetTraceLoggingApiModelsTraceLoggingPutUrl = () => {
  return `/api/models/trace-logging`;
};

/**
 * Toggle gateway trace-body capture for one model (TRA-1009).
 *
 * Rewrites the team's whole `traceLogging` object from the current
 * gateway state plus this one change. Enabling the first model on a
 * fully-off team seeds the denylist with every other known model id, so
 * nothing else silently starts capturing. A model created later under an
 * enabled team captures by default, matching the pre-TRA-1009 behavior.
 * @summary Set Trace Logging
 */
export const setTraceLoggingApiModelsTraceLoggingPut = async (
  traceLoggingModelUpdate: TraceLoggingModelUpdate,
  options?: Parameters<typeof orvalFetch>[1],
): Promise<setTraceLoggingApiModelsTraceLoggingPutResponse> => {
  const getHeaders = (
    h?: NonNullable<RequestInit["headers"]>,
  ): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(
          h as Iterable<Iterable<string>>,
          (entry) => Array.from(entry) as [string, string],
        ),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<
      string | readonly string[] | undefined
    >(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
  return orvalFetch<setTraceLoggingApiModelsTraceLoggingPutResponse>(
    getSetTraceLoggingApiModelsTraceLoggingPutUrl(),
    {
      ...options,
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        ...getHeaders(options?.headers),
      },
      body: JSON.stringify(traceLoggingModelUpdate),
    },
  );
};

export const getSetTraceLoggingApiModelsTraceLoggingPutMutationKey = () =>
  ["setTraceLoggingApiModelsTraceLoggingPut"] as const;

export const getSetTraceLoggingApiModelsTraceLoggingPutMutationOptions = <
  TError = ErrorType<HTTPValidationError>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof setTraceLoggingApiModelsTraceLoggingPut>>,
    TError,
    SetTraceLoggingApiModelsTraceLoggingPutMutationVariables,
    TContext
  >;
  request?: SecondParameter<typeof orvalFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof setTraceLoggingApiModelsTraceLoggingPut>>,
  TError,
  SetTraceLoggingApiModelsTraceLoggingPutMutationVariables,
  TContext
> => {
  const mutationKey = getSetTraceLoggingApiModelsTraceLoggingPutMutationKey();
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof setTraceLoggingApiModelsTraceLoggingPut>>,
    SetTraceLoggingApiModelsTraceLoggingPutMutationVariables
  > = (props) => {
    const { data } = props ?? {};

    return setTraceLoggingApiModelsTraceLoggingPut(data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type SetTraceLoggingApiModelsTraceLoggingPutMutationResult = NonNullable<
  Awaited<ReturnType<typeof setTraceLoggingApiModelsTraceLoggingPut>>
>;
export type SetTraceLoggingApiModelsTraceLoggingPutMutationBody =
  TraceLoggingModelUpdate;
export type SetTraceLoggingApiModelsTraceLoggingPutMutationError =
  ErrorType<HTTPValidationError>;
export type SetTraceLoggingApiModelsTraceLoggingPutMutationVariables = {
  data: TraceLoggingModelUpdate;
};

/**
 * @summary Set Trace Logging
 */
export const useSetTraceLoggingApiModelsTraceLoggingPut = <
  TError = ErrorType<HTTPValidationError>,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof setTraceLoggingApiModelsTraceLoggingPut>>,
      TError,
      SetTraceLoggingApiModelsTraceLoggingPutMutationVariables,
      TContext
    >;
    request?: SecondParameter<typeof orvalFetch>;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof setTraceLoggingApiModelsTraceLoggingPut>>,
  TError,
  SetTraceLoggingApiModelsTraceLoggingPutMutationVariables,
  TContext
> => {
  return useMutation(
    getSetTraceLoggingApiModelsTraceLoggingPutMutationOptions(options),
    queryClient,
  );
};
export type listModelsApiModelsGetResponse200 = {
  data: ModelResponse[];
  status: 200;
};

export type listModelsApiModelsGetResponse422 = {
  data: HTTPValidationError;
  status: 422;
};

export type listModelsApiModelsGetResponseSuccess =
  listModelsApiModelsGetResponse200 & {
    headers: Headers;
  };
export type listModelsApiModelsGetResponseError =
  listModelsApiModelsGetResponse422 & {
    headers: Headers;
  };

export type listModelsApiModelsGetResponse =
  | listModelsApiModelsGetResponseSuccess
  | listModelsApiModelsGetResponseError;

export const getListModelsApiModelsGetUrl = () => {
  return `/api/models`;
};

/**
 * List models for the current team.
 *
 * The gateway's modelRoutes is the source of truth for WHICH models
 * exist: this syncs the DB sidecar rows against it (importing
 * gateway-provisioned routes, pruning rows whose route is gone) and
 * returns the result. Under staff impersonation the sync would be a
 * WRITE on a read-only request, so we return the DB rows as-is.
 * @summary List Models
 */
export const listModelsApiModelsGet = async (
  options?: Parameters<typeof orvalFetch>[1],
): Promise<listModelsApiModelsGetResponse> => {
  return orvalFetch<listModelsApiModelsGetResponse>(
    getListModelsApiModelsGetUrl(),
    {
      ...options,
      method: "GET",
    },
  );
};

export const getListModelsApiModelsGetQueryKey = () => {
  return [`/api/models`] as const;
};

export const getListModelsApiModelsGetQueryOptions = <
  TData = Awaited<ReturnType<typeof listModelsApiModelsGet>>,
  TError = ErrorType<HTTPValidationError>,
>(options?: {
  query?: Partial<
    UseQueryOptions<
      Awaited<ReturnType<typeof listModelsApiModelsGet>>,
      TError,
      TData
    >
  >;
  request?: SecondParameter<typeof orvalFetch>;
}) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getListModelsApiModelsGetQueryKey();

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof listModelsApiModelsGet>>
  > = ({ signal }) => listModelsApiModelsGet({ signal, ...requestOptions });

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof listModelsApiModelsGet>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type ListModelsApiModelsGetQueryResult = NonNullable<
  Awaited<ReturnType<typeof listModelsApiModelsGet>>
>;
export type ListModelsApiModelsGetQueryError = ErrorType<HTTPValidationError>;

export function useListModelsApiModelsGet<
  TData = Awaited<ReturnType<typeof listModelsApiModelsGet>>,
  TError = ErrorType<HTTPValidationError>,
>(
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof listModelsApiModelsGet>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listModelsApiModelsGet>>,
          TError,
          Awaited<ReturnType<typeof listModelsApiModelsGet>>
        >,
        "initialData"
      >;
    request?: SecondParameter<typeof orvalFetch>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useListModelsApiModelsGet<
  TData = Awaited<ReturnType<typeof listModelsApiModelsGet>>,
  TError = ErrorType<HTTPValidationError>,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof listModelsApiModelsGet>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listModelsApiModelsGet>>,
          TError,
          Awaited<ReturnType<typeof listModelsApiModelsGet>>
        >,
        "initialData"
      >;
    request?: SecondParameter<typeof orvalFetch>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useListModelsApiModelsGet<
  TData = Awaited<ReturnType<typeof listModelsApiModelsGet>>,
  TError = ErrorType<HTTPValidationError>,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof listModelsApiModelsGet>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof orvalFetch>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
/**
 * @summary List Models
 */

export function useListModelsApiModelsGet<
  TData = Awaited<ReturnType<typeof listModelsApiModelsGet>>,
  TError = ErrorType<HTTPValidationError>,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof listModelsApiModelsGet>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof orvalFetch>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions = getListModelsApiModelsGetQueryOptions(options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}

export type createModelApiModelsPostResponse201 = {
  data: ModelResponse;
  status: 201;
};

export type createModelApiModelsPostResponse422 = {
  data: HTTPValidationError;
  status: 422;
};

export type createModelApiModelsPostResponseSuccess =
  createModelApiModelsPostResponse201 & {
    headers: Headers;
  };
export type createModelApiModelsPostResponseError =
  createModelApiModelsPostResponse422 & {
    headers: Headers;
  };

export type createModelApiModelsPostResponse =
  | createModelApiModelsPostResponseSuccess
  | createModelApiModelsPostResponseError;

export const getCreateModelApiModelsPostUrl = () => {
  return `/api/models`;
};

/**
 * Create a new model. Provisions team_credentials lazily on first model.
 *
 * Backing (validated to be exactly one by ModelCreate):
 * - catalog (`proxy_model_id`): route value is the catalog slug /
 *   private upstream id of the ProxyModel.
 * - custom (`custom`): a deployment slot `<team>/<slug>` is merged onto
 *   the chosen private upstream (per-slug baseUrl + provider model) and
 *   the route points at that upstream id.
 * @summary Create Model
 */
export const createModelApiModelsPost = async (
  modelCreate: ModelCreate,
  options?: Parameters<typeof orvalFetch>[1],
): Promise<createModelApiModelsPostResponse> => {
  const getHeaders = (
    h?: NonNullable<RequestInit["headers"]>,
  ): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(
          h as Iterable<Iterable<string>>,
          (entry) => Array.from(entry) as [string, string],
        ),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<
      string | readonly string[] | undefined
    >(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
  return orvalFetch<createModelApiModelsPostResponse>(
    getCreateModelApiModelsPostUrl(),
    {
      ...options,
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...getHeaders(options?.headers),
      },
      body: JSON.stringify(modelCreate),
    },
  );
};

export const getCreateModelApiModelsPostMutationKey = () =>
  ["createModelApiModelsPost"] as const;

export const getCreateModelApiModelsPostMutationOptions = <
  TError = ErrorType<HTTPValidationError>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof createModelApiModelsPost>>,
    TError,
    CreateModelApiModelsPostMutationVariables,
    TContext
  >;
  request?: SecondParameter<typeof orvalFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof createModelApiModelsPost>>,
  TError,
  CreateModelApiModelsPostMutationVariables,
  TContext
> => {
  const mutationKey = getCreateModelApiModelsPostMutationKey();
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof createModelApiModelsPost>>,
    CreateModelApiModelsPostMutationVariables
  > = (props) => {
    const { data } = props ?? {};

    return createModelApiModelsPost(data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type CreateModelApiModelsPostMutationResult = NonNullable<
  Awaited<ReturnType<typeof createModelApiModelsPost>>
>;
export type CreateModelApiModelsPostMutationBody = ModelCreate;
export type CreateModelApiModelsPostMutationError =
  ErrorType<HTTPValidationError>;
export type CreateModelApiModelsPostMutationVariables = { data: ModelCreate };

/**
 * @summary Create Model
 */
export const useCreateModelApiModelsPost = <
  TError = ErrorType<HTTPValidationError>,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof createModelApiModelsPost>>,
      TError,
      CreateModelApiModelsPostMutationVariables,
      TContext
    >;
    request?: SecondParameter<typeof orvalFetch>;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof createModelApiModelsPost>>,
  TError,
  CreateModelApiModelsPostMutationVariables,
  TContext
> => {
  return useMutation(
    getCreateModelApiModelsPostMutationOptions(options),
    queryClient,
  );
};
export type getModelApiModelsModelIdGetResponse200 = {
  data: ModelResponse;
  status: 200;
};

export type getModelApiModelsModelIdGetResponse422 = {
  data: HTTPValidationError;
  status: 422;
};

export type getModelApiModelsModelIdGetResponseSuccess =
  getModelApiModelsModelIdGetResponse200 & {
    headers: Headers;
  };
export type getModelApiModelsModelIdGetResponseError =
  getModelApiModelsModelIdGetResponse422 & {
    headers: Headers;
  };

export type getModelApiModelsModelIdGetResponse =
  | getModelApiModelsModelIdGetResponseSuccess
  | getModelApiModelsModelIdGetResponseError;

export const getGetModelApiModelsModelIdGetUrl = (modelId: string) => {
  return `/api/models/${modelId}`;
};

/**
 * @summary Get Model
 */
export const getModelApiModelsModelIdGet = async (
  modelId: string,
  options?: Parameters<typeof orvalFetch>[1],
): Promise<getModelApiModelsModelIdGetResponse> => {
  return orvalFetch<getModelApiModelsModelIdGetResponse>(
    getGetModelApiModelsModelIdGetUrl(modelId),
    {
      ...options,
      method: "GET",
    },
  );
};

export const getGetModelApiModelsModelIdGetQueryKey = (modelId: string) => {
  return [`/api/models/${modelId}`] as const;
};

export const getGetModelApiModelsModelIdGetQueryOptions = <
  TData = Awaited<ReturnType<typeof getModelApiModelsModelIdGet>>,
  TError = ErrorType<HTTPValidationError>,
>(
  modelId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getModelApiModelsModelIdGet>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof orvalFetch>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getGetModelApiModelsModelIdGetQueryKey(modelId);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getModelApiModelsModelIdGet>>
  > = ({ signal }) =>
    getModelApiModelsModelIdGet(modelId, { signal, ...requestOptions });

  return {
    queryKey,
    queryFn,
    enabled: modelId !== null && modelId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<
    Awaited<ReturnType<typeof getModelApiModelsModelIdGet>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetModelApiModelsModelIdGetQueryResult = NonNullable<
  Awaited<ReturnType<typeof getModelApiModelsModelIdGet>>
>;
export type GetModelApiModelsModelIdGetQueryError =
  ErrorType<HTTPValidationError>;

export function useGetModelApiModelsModelIdGet<
  TData = Awaited<ReturnType<typeof getModelApiModelsModelIdGet>>,
  TError = ErrorType<HTTPValidationError>,
>(
  modelId: string,
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getModelApiModelsModelIdGet>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getModelApiModelsModelIdGet>>,
          TError,
          Awaited<ReturnType<typeof getModelApiModelsModelIdGet>>
        >,
        "initialData"
      >;
    request?: SecondParameter<typeof orvalFetch>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetModelApiModelsModelIdGet<
  TData = Awaited<ReturnType<typeof getModelApiModelsModelIdGet>>,
  TError = ErrorType<HTTPValidationError>,
>(
  modelId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getModelApiModelsModelIdGet>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getModelApiModelsModelIdGet>>,
          TError,
          Awaited<ReturnType<typeof getModelApiModelsModelIdGet>>
        >,
        "initialData"
      >;
    request?: SecondParameter<typeof orvalFetch>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetModelApiModelsModelIdGet<
  TData = Awaited<ReturnType<typeof getModelApiModelsModelIdGet>>,
  TError = ErrorType<HTTPValidationError>,
>(
  modelId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getModelApiModelsModelIdGet>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof orvalFetch>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
/**
 * @summary Get Model
 */

export function useGetModelApiModelsModelIdGet<
  TData = Awaited<ReturnType<typeof getModelApiModelsModelIdGet>>,
  TError = ErrorType<HTTPValidationError>,
>(
  modelId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getModelApiModelsModelIdGet>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof orvalFetch>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions = getGetModelApiModelsModelIdGetQueryOptions(
    modelId,
    options,
  );

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}

export type updateModelApiModelsModelIdPatchResponse200 = {
  data: ModelResponse;
  status: 200;
};

export type updateModelApiModelsModelIdPatchResponse422 = {
  data: HTTPValidationError;
  status: 422;
};

export type updateModelApiModelsModelIdPatchResponseSuccess =
  updateModelApiModelsModelIdPatchResponse200 & {
    headers: Headers;
  };
export type updateModelApiModelsModelIdPatchResponseError =
  updateModelApiModelsModelIdPatchResponse422 & {
    headers: Headers;
  };

export type updateModelApiModelsModelIdPatchResponse =
  | updateModelApiModelsModelIdPatchResponseSuccess
  | updateModelApiModelsModelIdPatchResponseError;

export const getUpdateModelApiModelsModelIdPatchUrl = (modelId: string) => {
  return `/api/models/${modelId}`;
};

/**
 * @summary Update Model
 */
export const updateModelApiModelsModelIdPatch = async (
  modelId: string,
  modelUpdate: ModelUpdate,
  options?: Parameters<typeof orvalFetch>[1],
): Promise<updateModelApiModelsModelIdPatchResponse> => {
  const getHeaders = (
    h?: NonNullable<RequestInit["headers"]>,
  ): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(
          h as Iterable<Iterable<string>>,
          (entry) => Array.from(entry) as [string, string],
        ),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<
      string | readonly string[] | undefined
    >(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
  return orvalFetch<updateModelApiModelsModelIdPatchResponse>(
    getUpdateModelApiModelsModelIdPatchUrl(modelId),
    {
      ...options,
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        ...getHeaders(options?.headers),
      },
      body: JSON.stringify(modelUpdate),
    },
  );
};

export const getUpdateModelApiModelsModelIdPatchMutationKey = () =>
  ["updateModelApiModelsModelIdPatch"] as const;

export const getUpdateModelApiModelsModelIdPatchMutationOptions = <
  TError = ErrorType<HTTPValidationError>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof updateModelApiModelsModelIdPatch>>,
    TError,
    UpdateModelApiModelsModelIdPatchMutationVariables,
    TContext
  >;
  request?: SecondParameter<typeof orvalFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof updateModelApiModelsModelIdPatch>>,
  TError,
  UpdateModelApiModelsModelIdPatchMutationVariables,
  TContext
> => {
  const mutationKey = getUpdateModelApiModelsModelIdPatchMutationKey();
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof updateModelApiModelsModelIdPatch>>,
    UpdateModelApiModelsModelIdPatchMutationVariables
  > = (props) => {
    const { modelId, data } = props ?? {};

    return updateModelApiModelsModelIdPatch(modelId, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type UpdateModelApiModelsModelIdPatchMutationResult = NonNullable<
  Awaited<ReturnType<typeof updateModelApiModelsModelIdPatch>>
>;
export type UpdateModelApiModelsModelIdPatchMutationBody = ModelUpdate;
export type UpdateModelApiModelsModelIdPatchMutationError =
  ErrorType<HTTPValidationError>;
export type UpdateModelApiModelsModelIdPatchMutationVariables = {
  modelId: string;
  data: ModelUpdate;
};

/**
 * @summary Update Model
 */
export const useUpdateModelApiModelsModelIdPatch = <
  TError = ErrorType<HTTPValidationError>,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof updateModelApiModelsModelIdPatch>>,
      TError,
      UpdateModelApiModelsModelIdPatchMutationVariables,
      TContext
    >;
    request?: SecondParameter<typeof orvalFetch>;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof updateModelApiModelsModelIdPatch>>,
  TError,
  UpdateModelApiModelsModelIdPatchMutationVariables,
  TContext
> => {
  return useMutation(
    getUpdateModelApiModelsModelIdPatchMutationOptions(options),
    queryClient,
  );
};
export type deleteModelApiModelsModelIdDeleteResponse200 = {
  data: DeleteModelApiModelsModelIdDelete200;
  status: 200;
};

export type deleteModelApiModelsModelIdDeleteResponse422 = {
  data: HTTPValidationError;
  status: 422;
};

export type deleteModelApiModelsModelIdDeleteResponseSuccess =
  deleteModelApiModelsModelIdDeleteResponse200 & {
    headers: Headers;
  };
export type deleteModelApiModelsModelIdDeleteResponseError =
  deleteModelApiModelsModelIdDeleteResponse422 & {
    headers: Headers;
  };

export type deleteModelApiModelsModelIdDeleteResponse =
  | deleteModelApiModelsModelIdDeleteResponseSuccess
  | deleteModelApiModelsModelIdDeleteResponseError;

export const getDeleteModelApiModelsModelIdDeleteUrl = (modelId: string) => {
  return `/api/models/${modelId}`;
};

/**
 * @summary Delete Model
 */
export const deleteModelApiModelsModelIdDelete = async (
  modelId: string,
  options?: Parameters<typeof orvalFetch>[1],
): Promise<deleteModelApiModelsModelIdDeleteResponse> => {
  return orvalFetch<deleteModelApiModelsModelIdDeleteResponse>(
    getDeleteModelApiModelsModelIdDeleteUrl(modelId),
    {
      ...options,
      method: "DELETE",
    },
  );
};

export const getDeleteModelApiModelsModelIdDeleteMutationKey = () =>
  ["deleteModelApiModelsModelIdDelete"] as const;

export const getDeleteModelApiModelsModelIdDeleteMutationOptions = <
  TError = ErrorType<HTTPValidationError>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof deleteModelApiModelsModelIdDelete>>,
    TError,
    DeleteModelApiModelsModelIdDeleteMutationVariables,
    TContext
  >;
  request?: SecondParameter<typeof orvalFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof deleteModelApiModelsModelIdDelete>>,
  TError,
  DeleteModelApiModelsModelIdDeleteMutationVariables,
  TContext
> => {
  const mutationKey = getDeleteModelApiModelsModelIdDeleteMutationKey();
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof deleteModelApiModelsModelIdDelete>>,
    DeleteModelApiModelsModelIdDeleteMutationVariables
  > = (props) => {
    const { modelId } = props ?? {};

    return deleteModelApiModelsModelIdDelete(modelId, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type DeleteModelApiModelsModelIdDeleteMutationResult = NonNullable<
  Awaited<ReturnType<typeof deleteModelApiModelsModelIdDelete>>
>;

export type DeleteModelApiModelsModelIdDeleteMutationError =
  ErrorType<HTTPValidationError>;
export type DeleteModelApiModelsModelIdDeleteMutationVariables = {
  modelId: string;
};

/**
 * @summary Delete Model
 */
export const useDeleteModelApiModelsModelIdDelete = <
  TError = ErrorType<HTTPValidationError>,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof deleteModelApiModelsModelIdDelete>>,
      TError,
      DeleteModelApiModelsModelIdDeleteMutationVariables,
      TContext
    >;
    request?: SecondParameter<typeof orvalFetch>;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof deleteModelApiModelsModelIdDelete>>,
  TError,
  DeleteModelApiModelsModelIdDeleteMutationVariables,
  TContext
> => {
  return useMutation(
    getDeleteModelApiModelsModelIdDeleteMutationOptions(options),
    queryClient,
  );
};
export type addModelAliasApiModelsModelIdAliasesPostResponse201 = {
  data: ModelResponse;
  status: 201;
};

export type addModelAliasApiModelsModelIdAliasesPostResponse422 = {
  data: HTTPValidationError;
  status: 422;
};

export type addModelAliasApiModelsModelIdAliasesPostResponseSuccess =
  addModelAliasApiModelsModelIdAliasesPostResponse201 & {
    headers: Headers;
  };
export type addModelAliasApiModelsModelIdAliasesPostResponseError =
  addModelAliasApiModelsModelIdAliasesPostResponse422 & {
    headers: Headers;
  };

export type addModelAliasApiModelsModelIdAliasesPostResponse =
  | addModelAliasApiModelsModelIdAliasesPostResponseSuccess
  | addModelAliasApiModelsModelIdAliasesPostResponseError;

export const getAddModelAliasApiModelsModelIdAliasesPostUrl = (
  modelId: string,
) => {
  return `/api/models/${modelId}/aliases`;
};

/**
 * Add a slug that resolves to this model's deployment (TRA-1154).
 *
 * Gateway route first (`alias:<team>/<primary>`), DB row second; the
 * route is removed again if the row cannot be written. The upstream
 * config is never touched.
 * @summary Add Model Alias
 */
export const addModelAliasApiModelsModelIdAliasesPost = async (
  modelId: string,
  modelAliasCreate: ModelAliasCreate,
  options?: Parameters<typeof orvalFetch>[1],
): Promise<addModelAliasApiModelsModelIdAliasesPostResponse> => {
  const getHeaders = (
    h?: NonNullable<RequestInit["headers"]>,
  ): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(
          h as Iterable<Iterable<string>>,
          (entry) => Array.from(entry) as [string, string],
        ),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<
      string | readonly string[] | undefined
    >(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
  return orvalFetch<addModelAliasApiModelsModelIdAliasesPostResponse>(
    getAddModelAliasApiModelsModelIdAliasesPostUrl(modelId),
    {
      ...options,
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...getHeaders(options?.headers),
      },
      body: JSON.stringify(modelAliasCreate),
    },
  );
};

export const getAddModelAliasApiModelsModelIdAliasesPostMutationKey = () =>
  ["addModelAliasApiModelsModelIdAliasesPost"] as const;

export const getAddModelAliasApiModelsModelIdAliasesPostMutationOptions = <
  TError = ErrorType<HTTPValidationError>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof addModelAliasApiModelsModelIdAliasesPost>>,
    TError,
    AddModelAliasApiModelsModelIdAliasesPostMutationVariables,
    TContext
  >;
  request?: SecondParameter<typeof orvalFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof addModelAliasApiModelsModelIdAliasesPost>>,
  TError,
  AddModelAliasApiModelsModelIdAliasesPostMutationVariables,
  TContext
> => {
  const mutationKey = getAddModelAliasApiModelsModelIdAliasesPostMutationKey();
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof addModelAliasApiModelsModelIdAliasesPost>>,
    AddModelAliasApiModelsModelIdAliasesPostMutationVariables
  > = (props) => {
    const { modelId, data } = props ?? {};

    return addModelAliasApiModelsModelIdAliasesPost(
      modelId,
      data,
      requestOptions,
    );
  };

  return { mutationFn, ...mutationOptions };
};

export type AddModelAliasApiModelsModelIdAliasesPostMutationResult =
  NonNullable<
    Awaited<ReturnType<typeof addModelAliasApiModelsModelIdAliasesPost>>
  >;
export type AddModelAliasApiModelsModelIdAliasesPostMutationBody =
  ModelAliasCreate;
export type AddModelAliasApiModelsModelIdAliasesPostMutationError =
  ErrorType<HTTPValidationError>;
export type AddModelAliasApiModelsModelIdAliasesPostMutationVariables = {
  modelId: string;
  data: ModelAliasCreate;
};

/**
 * @summary Add Model Alias
 */
export const useAddModelAliasApiModelsModelIdAliasesPost = <
  TError = ErrorType<HTTPValidationError>,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof addModelAliasApiModelsModelIdAliasesPost>>,
      TError,
      AddModelAliasApiModelsModelIdAliasesPostMutationVariables,
      TContext
    >;
    request?: SecondParameter<typeof orvalFetch>;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof addModelAliasApiModelsModelIdAliasesPost>>,
  TError,
  AddModelAliasApiModelsModelIdAliasesPostMutationVariables,
  TContext
> => {
  return useMutation(
    getAddModelAliasApiModelsModelIdAliasesPostMutationOptions(options),
    queryClient,
  );
};
export type removeModelAliasApiModelsModelIdAliasesSlugDeleteResponse200 = {
  data: ModelResponse;
  status: 200;
};

export type removeModelAliasApiModelsModelIdAliasesSlugDeleteResponse422 = {
  data: HTTPValidationError;
  status: 422;
};

export type removeModelAliasApiModelsModelIdAliasesSlugDeleteResponseSuccess =
  removeModelAliasApiModelsModelIdAliasesSlugDeleteResponse200 & {
    headers: Headers;
  };
export type removeModelAliasApiModelsModelIdAliasesSlugDeleteResponseError =
  removeModelAliasApiModelsModelIdAliasesSlugDeleteResponse422 & {
    headers: Headers;
  };

export type removeModelAliasApiModelsModelIdAliasesSlugDeleteResponse =
  | removeModelAliasApiModelsModelIdAliasesSlugDeleteResponseSuccess
  | removeModelAliasApiModelsModelIdAliasesSlugDeleteResponseError;

export const getRemoveModelAliasApiModelsModelIdAliasesSlugDeleteUrl = (
  modelId: string,
  slug: string,
) => {
  return `/api/models/${modelId}/aliases/${slug}`;
};

/**
 * Remove an alias: route gone, row gone, upstream untouched.
 * @summary Remove Model Alias
 */
export const removeModelAliasApiModelsModelIdAliasesSlugDelete = async (
  modelId: string,
  slug: string,
  options?: Parameters<typeof orvalFetch>[1],
): Promise<removeModelAliasApiModelsModelIdAliasesSlugDeleteResponse> => {
  return orvalFetch<removeModelAliasApiModelsModelIdAliasesSlugDeleteResponse>(
    getRemoveModelAliasApiModelsModelIdAliasesSlugDeleteUrl(modelId, slug),
    {
      ...options,
      method: "DELETE",
    },
  );
};

export const getRemoveModelAliasApiModelsModelIdAliasesSlugDeleteMutationKey =
  () => ["removeModelAliasApiModelsModelIdAliasesSlugDelete"] as const;

export const getRemoveModelAliasApiModelsModelIdAliasesSlugDeleteMutationOptions =
  <TError = ErrorType<HTTPValidationError>, TContext = unknown>(options?: {
    mutation?: UseMutationOptions<
      Awaited<
        ReturnType<typeof removeModelAliasApiModelsModelIdAliasesSlugDelete>
      >,
      TError,
      RemoveModelAliasApiModelsModelIdAliasesSlugDeleteMutationVariables,
      TContext
    >;
    request?: SecondParameter<typeof orvalFetch>;
  }): UseMutationOptions<
    Awaited<
      ReturnType<typeof removeModelAliasApiModelsModelIdAliasesSlugDelete>
    >,
    TError,
    RemoveModelAliasApiModelsModelIdAliasesSlugDeleteMutationVariables,
    TContext
  > => {
    const mutationKey =
      getRemoveModelAliasApiModelsModelIdAliasesSlugDeleteMutationKey();
    const { mutation: mutationOptions, request: requestOptions } = options
      ? options.mutation &&
        "mutationKey" in options.mutation &&
        options.mutation.mutationKey
        ? options
        : { ...options, mutation: { ...options.mutation, mutationKey } }
      : { mutation: { mutationKey }, request: undefined };

    const mutationFn: MutationFunction<
      Awaited<
        ReturnType<typeof removeModelAliasApiModelsModelIdAliasesSlugDelete>
      >,
      RemoveModelAliasApiModelsModelIdAliasesSlugDeleteMutationVariables
    > = (props) => {
      const { modelId, slug } = props ?? {};

      return removeModelAliasApiModelsModelIdAliasesSlugDelete(
        modelId,
        slug,
        requestOptions,
      );
    };

    return { mutationFn, ...mutationOptions };
  };

export type RemoveModelAliasApiModelsModelIdAliasesSlugDeleteMutationResult =
  NonNullable<
    Awaited<
      ReturnType<typeof removeModelAliasApiModelsModelIdAliasesSlugDelete>
    >
  >;

export type RemoveModelAliasApiModelsModelIdAliasesSlugDeleteMutationError =
  ErrorType<HTTPValidationError>;
export type RemoveModelAliasApiModelsModelIdAliasesSlugDeleteMutationVariables =
  { modelId: string; slug: string };

/**
 * @summary Remove Model Alias
 */
export const useRemoveModelAliasApiModelsModelIdAliasesSlugDelete = <
  TError = ErrorType<HTTPValidationError>,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<
        ReturnType<typeof removeModelAliasApiModelsModelIdAliasesSlugDelete>
      >,
      TError,
      RemoveModelAliasApiModelsModelIdAliasesSlugDeleteMutationVariables,
      TContext
    >;
    request?: SecondParameter<typeof orvalFetch>;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof removeModelAliasApiModelsModelIdAliasesSlugDelete>>,
  TError,
  RemoveModelAliasApiModelsModelIdAliasesSlugDeleteMutationVariables,
  TContext
> => {
  return useMutation(
    getRemoveModelAliasApiModelsModelIdAliasesSlugDeleteMutationOptions(
      options,
    ),
    queryClient,
  );
};
export type setDefaultModelApiModelsModelIdDefaultPostResponse200 = {
  data: ModelResponse;
  status: 200;
};

export type setDefaultModelApiModelsModelIdDefaultPostResponse422 = {
  data: HTTPValidationError;
  status: 422;
};

export type setDefaultModelApiModelsModelIdDefaultPostResponseSuccess =
  setDefaultModelApiModelsModelIdDefaultPostResponse200 & {
    headers: Headers;
  };
export type setDefaultModelApiModelsModelIdDefaultPostResponseError =
  setDefaultModelApiModelsModelIdDefaultPostResponse422 & {
    headers: Headers;
  };

export type setDefaultModelApiModelsModelIdDefaultPostResponse =
  | setDefaultModelApiModelsModelIdDefaultPostResponseSuccess
  | setDefaultModelApiModelsModelIdDefaultPostResponseError;

export const getSetDefaultModelApiModelsModelIdDefaultPostUrl = (
  modelId: string,
) => {
  return `/api/models/${modelId}/default`;
};

/**
 * Make this the team's default deployed model (TRA-1215).
 *
 * The nightly evals score whichever model carries the flag, so promoting
 * a retrain is this one call. A Model row exists only while its gateway
 * route does (GET /models prunes the rest), so the default is always
 * routable. Idempotent on the current default. Two promotions racing
 * both clear the old flag and each set their own; the partial unique
 * index refuses the second, which is the 409 here. A Postgres-only
 * change, so its audit row rides the same transaction (TRA-1626).
 * @summary Set Default Model
 */
export const setDefaultModelApiModelsModelIdDefaultPost = async (
  modelId: string,
  options?: Parameters<typeof orvalFetch>[1],
): Promise<setDefaultModelApiModelsModelIdDefaultPostResponse> => {
  return orvalFetch<setDefaultModelApiModelsModelIdDefaultPostResponse>(
    getSetDefaultModelApiModelsModelIdDefaultPostUrl(modelId),
    {
      ...options,
      method: "POST",
    },
  );
};

export const getSetDefaultModelApiModelsModelIdDefaultPostMutationKey = () =>
  ["setDefaultModelApiModelsModelIdDefaultPost"] as const;

export const getSetDefaultModelApiModelsModelIdDefaultPostMutationOptions = <
  TError = ErrorType<HTTPValidationError>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof setDefaultModelApiModelsModelIdDefaultPost>>,
    TError,
    SetDefaultModelApiModelsModelIdDefaultPostMutationVariables,
    TContext
  >;
  request?: SecondParameter<typeof orvalFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof setDefaultModelApiModelsModelIdDefaultPost>>,
  TError,
  SetDefaultModelApiModelsModelIdDefaultPostMutationVariables,
  TContext
> => {
  const mutationKey =
    getSetDefaultModelApiModelsModelIdDefaultPostMutationKey();
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof setDefaultModelApiModelsModelIdDefaultPost>>,
    SetDefaultModelApiModelsModelIdDefaultPostMutationVariables
  > = (props) => {
    const { modelId } = props ?? {};

    return setDefaultModelApiModelsModelIdDefaultPost(modelId, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type SetDefaultModelApiModelsModelIdDefaultPostMutationResult =
  NonNullable<
    Awaited<ReturnType<typeof setDefaultModelApiModelsModelIdDefaultPost>>
  >;

export type SetDefaultModelApiModelsModelIdDefaultPostMutationError =
  ErrorType<HTTPValidationError>;
export type SetDefaultModelApiModelsModelIdDefaultPostMutationVariables = {
  modelId: string;
};

/**
 * @summary Set Default Model
 */
export const useSetDefaultModelApiModelsModelIdDefaultPost = <
  TError = ErrorType<HTTPValidationError>,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof setDefaultModelApiModelsModelIdDefaultPost>>,
      TError,
      SetDefaultModelApiModelsModelIdDefaultPostMutationVariables,
      TContext
    >;
    request?: SecondParameter<typeof orvalFetch>;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof setDefaultModelApiModelsModelIdDefaultPost>>,
  TError,
  SetDefaultModelApiModelsModelIdDefaultPostMutationVariables,
  TContext
> => {
  return useMutation(
    getSetDefaultModelApiModelsModelIdDefaultPostMutationOptions(options),
    queryClient,
  );
};
export type setLatencyAlertsApiModelsModelIdLatencyAlertsPatchResponse200 = {
  data: ModelResponse;
  status: 200;
};

export type setLatencyAlertsApiModelsModelIdLatencyAlertsPatchResponse422 = {
  data: HTTPValidationError;
  status: 422;
};

export type setLatencyAlertsApiModelsModelIdLatencyAlertsPatchResponseSuccess =
  setLatencyAlertsApiModelsModelIdLatencyAlertsPatchResponse200 & {
    headers: Headers;
  };
export type setLatencyAlertsApiModelsModelIdLatencyAlertsPatchResponseError =
  setLatencyAlertsApiModelsModelIdLatencyAlertsPatchResponse422 & {
    headers: Headers;
  };

export type setLatencyAlertsApiModelsModelIdLatencyAlertsPatchResponse =
  | setLatencyAlertsApiModelsModelIdLatencyAlertsPatchResponseSuccess
  | setLatencyAlertsApiModelsModelIdLatencyAlertsPatchResponseError;

export const getSetLatencyAlertsApiModelsModelIdLatencyAlertsPatchUrl = (
  modelId: string,
) => {
  return `/api/models/${modelId}/latency-alerts`;
};

/**
 * Turn the latency alerts for this model on or off (TRA-1414).
 *
 * The latency-alerts CronJob reads the flag on every run, so the change
 * takes effect on its next run with no deploy. Idempotent.
 * @summary Set Latency Alerts
 */
export const setLatencyAlertsApiModelsModelIdLatencyAlertsPatch = async (
  modelId: string,
  latencyAlertsUpdate: LatencyAlertsUpdate,
  options?: Parameters<typeof orvalFetch>[1],
): Promise<setLatencyAlertsApiModelsModelIdLatencyAlertsPatchResponse> => {
  const getHeaders = (
    h?: NonNullable<RequestInit["headers"]>,
  ): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(
          h as Iterable<Iterable<string>>,
          (entry) => Array.from(entry) as [string, string],
        ),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<
      string | readonly string[] | undefined
    >(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
  return orvalFetch<setLatencyAlertsApiModelsModelIdLatencyAlertsPatchResponse>(
    getSetLatencyAlertsApiModelsModelIdLatencyAlertsPatchUrl(modelId),
    {
      ...options,
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        ...getHeaders(options?.headers),
      },
      body: JSON.stringify(latencyAlertsUpdate),
    },
  );
};

export const getSetLatencyAlertsApiModelsModelIdLatencyAlertsPatchMutationKey =
  () => ["setLatencyAlertsApiModelsModelIdLatencyAlertsPatch"] as const;

export const getSetLatencyAlertsApiModelsModelIdLatencyAlertsPatchMutationOptions =
  <TError = ErrorType<HTTPValidationError>, TContext = unknown>(options?: {
    mutation?: UseMutationOptions<
      Awaited<
        ReturnType<typeof setLatencyAlertsApiModelsModelIdLatencyAlertsPatch>
      >,
      TError,
      SetLatencyAlertsApiModelsModelIdLatencyAlertsPatchMutationVariables,
      TContext
    >;
    request?: SecondParameter<typeof orvalFetch>;
  }): UseMutationOptions<
    Awaited<
      ReturnType<typeof setLatencyAlertsApiModelsModelIdLatencyAlertsPatch>
    >,
    TError,
    SetLatencyAlertsApiModelsModelIdLatencyAlertsPatchMutationVariables,
    TContext
  > => {
    const mutationKey =
      getSetLatencyAlertsApiModelsModelIdLatencyAlertsPatchMutationKey();
    const { mutation: mutationOptions, request: requestOptions } = options
      ? options.mutation &&
        "mutationKey" in options.mutation &&
        options.mutation.mutationKey
        ? options
        : { ...options, mutation: { ...options.mutation, mutationKey } }
      : { mutation: { mutationKey }, request: undefined };

    const mutationFn: MutationFunction<
      Awaited<
        ReturnType<typeof setLatencyAlertsApiModelsModelIdLatencyAlertsPatch>
      >,
      SetLatencyAlertsApiModelsModelIdLatencyAlertsPatchMutationVariables
    > = (props) => {
      const { modelId, data } = props ?? {};

      return setLatencyAlertsApiModelsModelIdLatencyAlertsPatch(
        modelId,
        data,
        requestOptions,
      );
    };

    return { mutationFn, ...mutationOptions };
  };

export type SetLatencyAlertsApiModelsModelIdLatencyAlertsPatchMutationResult =
  NonNullable<
    Awaited<
      ReturnType<typeof setLatencyAlertsApiModelsModelIdLatencyAlertsPatch>
    >
  >;
export type SetLatencyAlertsApiModelsModelIdLatencyAlertsPatchMutationBody =
  LatencyAlertsUpdate;
export type SetLatencyAlertsApiModelsModelIdLatencyAlertsPatchMutationError =
  ErrorType<HTTPValidationError>;
export type SetLatencyAlertsApiModelsModelIdLatencyAlertsPatchMutationVariables =
  { modelId: string; data: LatencyAlertsUpdate };

/**
 * @summary Set Latency Alerts
 */
export const useSetLatencyAlertsApiModelsModelIdLatencyAlertsPatch = <
  TError = ErrorType<HTTPValidationError>,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<
        ReturnType<typeof setLatencyAlertsApiModelsModelIdLatencyAlertsPatch>
      >,
      TError,
      SetLatencyAlertsApiModelsModelIdLatencyAlertsPatchMutationVariables,
      TContext
    >;
    request?: SecondParameter<typeof orvalFetch>;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<
    ReturnType<typeof setLatencyAlertsApiModelsModelIdLatencyAlertsPatch>
  >,
  TError,
  SetLatencyAlertsApiModelsModelIdLatencyAlertsPatchMutationVariables,
  TContext
> => {
  return useMutation(
    getSetLatencyAlertsApiModelsModelIdLatencyAlertsPatchMutationOptions(
      options,
    ),
    queryClient,
  );
};
export type promoteModelAliasApiModelsModelIdAliasesSlugPromotePostResponse200 =
  {
    data: ModelResponse;
    status: 200;
  };

export type promoteModelAliasApiModelsModelIdAliasesSlugPromotePostResponse422 =
  {
    data: HTTPValidationError;
    status: 422;
  };

export type promoteModelAliasApiModelsModelIdAliasesSlugPromotePostResponseSuccess =
  promoteModelAliasApiModelsModelIdAliasesSlugPromotePostResponse200 & {
    headers: Headers;
  };
export type promoteModelAliasApiModelsModelIdAliasesSlugPromotePostResponseError =
  promoteModelAliasApiModelsModelIdAliasesSlugPromotePostResponse422 & {
    headers: Headers;
  };

export type promoteModelAliasApiModelsModelIdAliasesSlugPromotePostResponse =
  | promoteModelAliasApiModelsModelIdAliasesSlugPromotePostResponseSuccess
  | promoteModelAliasApiModelsModelIdAliasesSlugPromotePostResponseError;

export const getPromoteModelAliasApiModelsModelIdAliasesSlugPromotePostUrl = (
  modelId: string,
  slug: string,
) => {
  return `/api/models/${modelId}/aliases/${slug}/promote`;
};

/**
 * Make an alias the primary slug; the old primary becomes an alias.
 *
 * Gateway first (route rewrite, then slot moves, with undo), DB rows
 * swapped last. Usage history stays on this one model either way.
 * @summary Promote Model Alias
 */
export const promoteModelAliasApiModelsModelIdAliasesSlugPromotePost = async (
  modelId: string,
  slug: string,
  options?: Parameters<typeof orvalFetch>[1],
): Promise<promoteModelAliasApiModelsModelIdAliasesSlugPromotePostResponse> => {
  return orvalFetch<promoteModelAliasApiModelsModelIdAliasesSlugPromotePostResponse>(
    getPromoteModelAliasApiModelsModelIdAliasesSlugPromotePostUrl(
      modelId,
      slug,
    ),
    {
      ...options,
      method: "POST",
    },
  );
};

export const getPromoteModelAliasApiModelsModelIdAliasesSlugPromotePostMutationKey =
  () => ["promoteModelAliasApiModelsModelIdAliasesSlugPromotePost"] as const;

export const getPromoteModelAliasApiModelsModelIdAliasesSlugPromotePostMutationOptions =
  <TError = ErrorType<HTTPValidationError>, TContext = unknown>(options?: {
    mutation?: UseMutationOptions<
      Awaited<
        ReturnType<
          typeof promoteModelAliasApiModelsModelIdAliasesSlugPromotePost
        >
      >,
      TError,
      PromoteModelAliasApiModelsModelIdAliasesSlugPromotePostMutationVariables,
      TContext
    >;
    request?: SecondParameter<typeof orvalFetch>;
  }): UseMutationOptions<
    Awaited<
      ReturnType<typeof promoteModelAliasApiModelsModelIdAliasesSlugPromotePost>
    >,
    TError,
    PromoteModelAliasApiModelsModelIdAliasesSlugPromotePostMutationVariables,
    TContext
  > => {
    const mutationKey =
      getPromoteModelAliasApiModelsModelIdAliasesSlugPromotePostMutationKey();
    const { mutation: mutationOptions, request: requestOptions } = options
      ? options.mutation &&
        "mutationKey" in options.mutation &&
        options.mutation.mutationKey
        ? options
        : { ...options, mutation: { ...options.mutation, mutationKey } }
      : { mutation: { mutationKey }, request: undefined };

    const mutationFn: MutationFunction<
      Awaited<
        ReturnType<
          typeof promoteModelAliasApiModelsModelIdAliasesSlugPromotePost
        >
      >,
      PromoteModelAliasApiModelsModelIdAliasesSlugPromotePostMutationVariables
    > = (props) => {
      const { modelId, slug } = props ?? {};

      return promoteModelAliasApiModelsModelIdAliasesSlugPromotePost(
        modelId,
        slug,
        requestOptions,
      );
    };

    return { mutationFn, ...mutationOptions };
  };

export type PromoteModelAliasApiModelsModelIdAliasesSlugPromotePostMutationResult =
  NonNullable<
    Awaited<
      ReturnType<typeof promoteModelAliasApiModelsModelIdAliasesSlugPromotePost>
    >
  >;

export type PromoteModelAliasApiModelsModelIdAliasesSlugPromotePostMutationError =
  ErrorType<HTTPValidationError>;
export type PromoteModelAliasApiModelsModelIdAliasesSlugPromotePostMutationVariables =
  { modelId: string; slug: string };

/**
 * @summary Promote Model Alias
 */
export const usePromoteModelAliasApiModelsModelIdAliasesSlugPromotePost = <
  TError = ErrorType<HTTPValidationError>,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<
        ReturnType<
          typeof promoteModelAliasApiModelsModelIdAliasesSlugPromotePost
        >
      >,
      TError,
      PromoteModelAliasApiModelsModelIdAliasesSlugPromotePostMutationVariables,
      TContext
    >;
    request?: SecondParameter<typeof orvalFetch>;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<
    ReturnType<typeof promoteModelAliasApiModelsModelIdAliasesSlugPromotePost>
  >,
  TError,
  PromoteModelAliasApiModelsModelIdAliasesSlugPromotePostMutationVariables,
  TContext
> => {
  return useMutation(
    getPromoteModelAliasApiModelsModelIdAliasesSlugPromotePostMutationOptions(
      options,
    ),
    queryClient,
  );
};
export type getFallbackOptionsApiModelsModelIdFallbackOptionsGetResponse200 = {
  data: FallbackOptionsResponse;
  status: 200;
};

export type getFallbackOptionsApiModelsModelIdFallbackOptionsGetResponse422 = {
  data: HTTPValidationError;
  status: 422;
};

export type getFallbackOptionsApiModelsModelIdFallbackOptionsGetResponseSuccess =
  getFallbackOptionsApiModelsModelIdFallbackOptionsGetResponse200 & {
    headers: Headers;
  };
export type getFallbackOptionsApiModelsModelIdFallbackOptionsGetResponseError =
  getFallbackOptionsApiModelsModelIdFallbackOptionsGetResponse422 & {
    headers: Headers;
  };

export type getFallbackOptionsApiModelsModelIdFallbackOptionsGetResponse =
  | getFallbackOptionsApiModelsModelIdFallbackOptionsGetResponseSuccess
  | getFallbackOptionsApiModelsModelIdFallbackOptionsGetResponseError;

export const getGetFallbackOptionsApiModelsModelIdFallbackOptionsGetUrl = (
  modelId: string,
) => {
  return `/api/models/${modelId}/fallback-options`;
};

/**
 * Everything the fallback-chain editor needs for one model.
 *
 * `primary` is the model's current gateway route value (the chain must
 * not repeat it). `catalog` is the public catalog (valid chain values as
 * slugs); `upstreams` is each visible private upstream, flagged with
 * whether it already carries a deployment keyed by THIS model's
 * canonical slug — a bare-id fallback without that slot 404s at fallback
 * time, so the UI blocks those targets. `billing` is how the gateway
 * prices the model, read off the same team fetch (TRA-1383), and
 * `gpu_model_ids` the Baseten model ids the team's GPU time is billed
 * under (TRA-1422).
 * @summary Get Fallback Options
 */
export const getFallbackOptionsApiModelsModelIdFallbackOptionsGet = async (
  modelId: string,
  options?: Parameters<typeof orvalFetch>[1],
): Promise<getFallbackOptionsApiModelsModelIdFallbackOptionsGetResponse> => {
  return orvalFetch<getFallbackOptionsApiModelsModelIdFallbackOptionsGetResponse>(
    getGetFallbackOptionsApiModelsModelIdFallbackOptionsGetUrl(modelId),
    {
      ...options,
      method: "GET",
    },
  );
};

export const getGetFallbackOptionsApiModelsModelIdFallbackOptionsGetQueryKey = (
  modelId: string,
) => {
  return [`/api/models/${modelId}/fallback-options`] as const;
};

export const getGetFallbackOptionsApiModelsModelIdFallbackOptionsGetQueryOptions =
  <
    TData = Awaited<
      ReturnType<typeof getFallbackOptionsApiModelsModelIdFallbackOptionsGet>
    >,
    TError = ErrorType<HTTPValidationError>,
  >(
    modelId: string,
    options?: {
      query?: Partial<
        UseQueryOptions<
          Awaited<
            ReturnType<
              typeof getFallbackOptionsApiModelsModelIdFallbackOptionsGet
            >
          >,
          TError,
          TData
        >
      >;
      request?: SecondParameter<typeof orvalFetch>;
    },
  ) => {
    const { query: queryOptions, request: requestOptions } = options ?? {};

    const queryKey =
      queryOptions?.queryKey ??
      getGetFallbackOptionsApiModelsModelIdFallbackOptionsGetQueryKey(modelId);

    const queryFn: QueryFunction<
      Awaited<
        ReturnType<typeof getFallbackOptionsApiModelsModelIdFallbackOptionsGet>
      >
    > = ({ signal }) =>
      getFallbackOptionsApiModelsModelIdFallbackOptionsGet(modelId, {
        signal,
        ...requestOptions,
      });

    return {
      queryKey,
      queryFn,
      enabled: modelId !== null && modelId !== undefined,
      ...queryOptions,
    } as UseQueryOptions<
      Awaited<
        ReturnType<typeof getFallbackOptionsApiModelsModelIdFallbackOptionsGet>
      >,
      TError,
      TData
    > & { queryKey: DataTag<QueryKey, TData, TError> };
  };

export type GetFallbackOptionsApiModelsModelIdFallbackOptionsGetQueryResult =
  NonNullable<
    Awaited<
      ReturnType<typeof getFallbackOptionsApiModelsModelIdFallbackOptionsGet>
    >
  >;
export type GetFallbackOptionsApiModelsModelIdFallbackOptionsGetQueryError =
  ErrorType<HTTPValidationError>;

export function useGetFallbackOptionsApiModelsModelIdFallbackOptionsGet<
  TData = Awaited<
    ReturnType<typeof getFallbackOptionsApiModelsModelIdFallbackOptionsGet>
  >,
  TError = ErrorType<HTTPValidationError>,
>(
  modelId: string,
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<
            typeof getFallbackOptionsApiModelsModelIdFallbackOptionsGet
          >
        >,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<
            ReturnType<
              typeof getFallbackOptionsApiModelsModelIdFallbackOptionsGet
            >
          >,
          TError,
          Awaited<
            ReturnType<
              typeof getFallbackOptionsApiModelsModelIdFallbackOptionsGet
            >
          >
        >,
        "initialData"
      >;
    request?: SecondParameter<typeof orvalFetch>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetFallbackOptionsApiModelsModelIdFallbackOptionsGet<
  TData = Awaited<
    ReturnType<typeof getFallbackOptionsApiModelsModelIdFallbackOptionsGet>
  >,
  TError = ErrorType<HTTPValidationError>,
>(
  modelId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<
            typeof getFallbackOptionsApiModelsModelIdFallbackOptionsGet
          >
        >,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<
            ReturnType<
              typeof getFallbackOptionsApiModelsModelIdFallbackOptionsGet
            >
          >,
          TError,
          Awaited<
            ReturnType<
              typeof getFallbackOptionsApiModelsModelIdFallbackOptionsGet
            >
          >
        >,
        "initialData"
      >;
    request?: SecondParameter<typeof orvalFetch>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetFallbackOptionsApiModelsModelIdFallbackOptionsGet<
  TData = Awaited<
    ReturnType<typeof getFallbackOptionsApiModelsModelIdFallbackOptionsGet>
  >,
  TError = ErrorType<HTTPValidationError>,
>(
  modelId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<
            typeof getFallbackOptionsApiModelsModelIdFallbackOptionsGet
          >
        >,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof orvalFetch>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
/**
 * @summary Get Fallback Options
 */

export function useGetFallbackOptionsApiModelsModelIdFallbackOptionsGet<
  TData = Awaited<
    ReturnType<typeof getFallbackOptionsApiModelsModelIdFallbackOptionsGet>
  >,
  TError = ErrorType<HTTPValidationError>,
>(
  modelId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<
            typeof getFallbackOptionsApiModelsModelIdFallbackOptionsGet
          >
        >,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof orvalFetch>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions =
    getGetFallbackOptionsApiModelsModelIdFallbackOptionsGetQueryOptions(
      modelId,
      options,
    );

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}

export type getModelScalingApiModelsModelIdScalingGetResponse200 = {
  data: ModelScalingResponse;
  status: 200;
};

export type getModelScalingApiModelsModelIdScalingGetResponse422 = {
  data: HTTPValidationError;
  status: 422;
};

export type getModelScalingApiModelsModelIdScalingGetResponseSuccess =
  getModelScalingApiModelsModelIdScalingGetResponse200 & {
    headers: Headers;
  };
export type getModelScalingApiModelsModelIdScalingGetResponseError =
  getModelScalingApiModelsModelIdScalingGetResponse422 & {
    headers: Headers;
  };

export type getModelScalingApiModelsModelIdScalingGetResponse =
  | getModelScalingApiModelsModelIdScalingGetResponseSuccess
  | getModelScalingApiModelsModelIdScalingGetResponseError;

export const getGetModelScalingApiModelsModelIdScalingGetUrl = (
  modelId: string,
  params?: GetModelScalingApiModelsModelIdScalingGetParams,
) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {
    if (value !== undefined) {
      normalizedParams.append(key, value === null ? "null" : String(value));
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0
    ? `/api/models/${modelId}/scaling?${stringifiedParams}`
    : `/api/models/${modelId}/scaling`;
};

/**
 * Live Baseten scaling state for a dedicated-fleet model (TRA-1051).
 *
 * Status and replica count are read from the Baseten management API on
 * every call (via the gateway, which holds the key); history comes from
 * the gateway's per-tick samples.
 * @summary Get Model Scaling
 */
export const getModelScalingApiModelsModelIdScalingGet = async (
  modelId: string,
  params?: GetModelScalingApiModelsModelIdScalingGetParams,
  options?: Parameters<typeof orvalFetch>[1],
): Promise<getModelScalingApiModelsModelIdScalingGetResponse> => {
  return orvalFetch<getModelScalingApiModelsModelIdScalingGetResponse>(
    getGetModelScalingApiModelsModelIdScalingGetUrl(modelId, params),
    {
      ...options,
      method: "GET",
    },
  );
};

export const getGetModelScalingApiModelsModelIdScalingGetQueryKey = (
  modelId: string,
  params?: GetModelScalingApiModelsModelIdScalingGetParams,
) => {
  return [
    `/api/models/${modelId}/scaling`,
    ...(params ? [params] : []),
  ] as const;
};

export const getGetModelScalingApiModelsModelIdScalingGetQueryOptions = <
  TData = Awaited<ReturnType<typeof getModelScalingApiModelsModelIdScalingGet>>,
  TError = ErrorType<HTTPValidationError>,
>(
  modelId: string,
  params?: GetModelScalingApiModelsModelIdScalingGetParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getModelScalingApiModelsModelIdScalingGet>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof orvalFetch>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getGetModelScalingApiModelsModelIdScalingGetQueryKey(modelId, params);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getModelScalingApiModelsModelIdScalingGet>>
  > = ({ signal }) =>
    getModelScalingApiModelsModelIdScalingGet(modelId, params, {
      signal,
      ...requestOptions,
    });

  return {
    queryKey,
    queryFn,
    enabled: modelId !== null && modelId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<
    Awaited<ReturnType<typeof getModelScalingApiModelsModelIdScalingGet>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetModelScalingApiModelsModelIdScalingGetQueryResult = NonNullable<
  Awaited<ReturnType<typeof getModelScalingApiModelsModelIdScalingGet>>
>;
export type GetModelScalingApiModelsModelIdScalingGetQueryError =
  ErrorType<HTTPValidationError>;

export function useGetModelScalingApiModelsModelIdScalingGet<
  TData = Awaited<ReturnType<typeof getModelScalingApiModelsModelIdScalingGet>>,
  TError = ErrorType<HTTPValidationError>,
>(
  modelId: string,
  params: undefined | GetModelScalingApiModelsModelIdScalingGetParams,
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getModelScalingApiModelsModelIdScalingGet>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getModelScalingApiModelsModelIdScalingGet>>,
          TError,
          Awaited<ReturnType<typeof getModelScalingApiModelsModelIdScalingGet>>
        >,
        "initialData"
      >;
    request?: SecondParameter<typeof orvalFetch>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetModelScalingApiModelsModelIdScalingGet<
  TData = Awaited<ReturnType<typeof getModelScalingApiModelsModelIdScalingGet>>,
  TError = ErrorType<HTTPValidationError>,
>(
  modelId: string,
  params?: GetModelScalingApiModelsModelIdScalingGetParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getModelScalingApiModelsModelIdScalingGet>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getModelScalingApiModelsModelIdScalingGet>>,
          TError,
          Awaited<ReturnType<typeof getModelScalingApiModelsModelIdScalingGet>>
        >,
        "initialData"
      >;
    request?: SecondParameter<typeof orvalFetch>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetModelScalingApiModelsModelIdScalingGet<
  TData = Awaited<ReturnType<typeof getModelScalingApiModelsModelIdScalingGet>>,
  TError = ErrorType<HTTPValidationError>,
>(
  modelId: string,
  params?: GetModelScalingApiModelsModelIdScalingGetParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getModelScalingApiModelsModelIdScalingGet>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof orvalFetch>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
/**
 * @summary Get Model Scaling
 */

export function useGetModelScalingApiModelsModelIdScalingGet<
  TData = Awaited<ReturnType<typeof getModelScalingApiModelsModelIdScalingGet>>,
  TError = ErrorType<HTTPValidationError>,
>(
  modelId: string,
  params?: GetModelScalingApiModelsModelIdScalingGetParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getModelScalingApiModelsModelIdScalingGet>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof orvalFetch>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions = getGetModelScalingApiModelsModelIdScalingGetQueryOptions(
    modelId,
    params,
    options,
  );

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}

export type updateModelScalingApiModelsModelIdScalingPutResponse200 = {
  data: ModelScalingResponse;
  status: 200;
};

export type updateModelScalingApiModelsModelIdScalingPutResponse422 = {
  data: HTTPValidationError;
  status: 422;
};

export type updateModelScalingApiModelsModelIdScalingPutResponseSuccess =
  updateModelScalingApiModelsModelIdScalingPutResponse200 & {
    headers: Headers;
  };
export type updateModelScalingApiModelsModelIdScalingPutResponseError =
  updateModelScalingApiModelsModelIdScalingPutResponse422 & {
    headers: Headers;
  };

export type updateModelScalingApiModelsModelIdScalingPutResponse =
  | updateModelScalingApiModelsModelIdScalingPutResponseSuccess
  | updateModelScalingApiModelsModelIdScalingPutResponseError;

export const getUpdateModelScalingApiModelsModelIdScalingPutUrl = (
  modelId: string,
) => {
  return `/api/models/${modelId}/scaling`;
};

/**
 * Set a dedicated Baseten deployment's autoscaling settings.
 *
 * Writes through the gateway to the Baseten management API, then re-reads
 * so the response is the post-write state. Restricted demo teams are
 * rejected by get_current_team_id before this runs. The state read before
 * the write, the re-read and the person saving go into the replica
 * setting history (TRA-1558) once the gateway has taken the write. When
 * the re-read fails the save answers with that failure, and the row
 * holds the requested state instead: Baseten has it either way.
 * @summary Update Model Scaling
 */
export const updateModelScalingApiModelsModelIdScalingPut = async (
  modelId: string,
  modelScalingUpdate: ModelScalingUpdate,
  options?: Parameters<typeof orvalFetch>[1],
): Promise<updateModelScalingApiModelsModelIdScalingPutResponse> => {
  const getHeaders = (
    h?: NonNullable<RequestInit["headers"]>,
  ): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(
          h as Iterable<Iterable<string>>,
          (entry) => Array.from(entry) as [string, string],
        ),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<
      string | readonly string[] | undefined
    >(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
  return orvalFetch<updateModelScalingApiModelsModelIdScalingPutResponse>(
    getUpdateModelScalingApiModelsModelIdScalingPutUrl(modelId),
    {
      ...options,
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        ...getHeaders(options?.headers),
      },
      body: JSON.stringify(modelScalingUpdate),
    },
  );
};

export const getUpdateModelScalingApiModelsModelIdScalingPutMutationKey = () =>
  ["updateModelScalingApiModelsModelIdScalingPut"] as const;

export const getUpdateModelScalingApiModelsModelIdScalingPutMutationOptions = <
  TError = ErrorType<HTTPValidationError>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof updateModelScalingApiModelsModelIdScalingPut>>,
    TError,
    UpdateModelScalingApiModelsModelIdScalingPutMutationVariables,
    TContext
  >;
  request?: SecondParameter<typeof orvalFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof updateModelScalingApiModelsModelIdScalingPut>>,
  TError,
  UpdateModelScalingApiModelsModelIdScalingPutMutationVariables,
  TContext
> => {
  const mutationKey =
    getUpdateModelScalingApiModelsModelIdScalingPutMutationKey();
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof updateModelScalingApiModelsModelIdScalingPut>>,
    UpdateModelScalingApiModelsModelIdScalingPutMutationVariables
  > = (props) => {
    const { modelId, data } = props ?? {};

    return updateModelScalingApiModelsModelIdScalingPut(
      modelId,
      data,
      requestOptions,
    );
  };

  return { mutationFn, ...mutationOptions };
};

export type UpdateModelScalingApiModelsModelIdScalingPutMutationResult =
  NonNullable<
    Awaited<ReturnType<typeof updateModelScalingApiModelsModelIdScalingPut>>
  >;
export type UpdateModelScalingApiModelsModelIdScalingPutMutationBody =
  ModelScalingUpdate;
export type UpdateModelScalingApiModelsModelIdScalingPutMutationError =
  ErrorType<HTTPValidationError>;
export type UpdateModelScalingApiModelsModelIdScalingPutMutationVariables = {
  modelId: string;
  data: ModelScalingUpdate;
};

/**
 * @summary Update Model Scaling
 */
export const useUpdateModelScalingApiModelsModelIdScalingPut = <
  TError = ErrorType<HTTPValidationError>,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof updateModelScalingApiModelsModelIdScalingPut>>,
      TError,
      UpdateModelScalingApiModelsModelIdScalingPutMutationVariables,
      TContext
    >;
    request?: SecondParameter<typeof orvalFetch>;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof updateModelScalingApiModelsModelIdScalingPut>>,
  TError,
  UpdateModelScalingApiModelsModelIdScalingPutMutationVariables,
  TContext
> => {
  return useMutation(
    getUpdateModelScalingApiModelsModelIdScalingPutMutationOptions(options),
    queryClient,
  );
};
export type getModelEngineMetricsApiModelsModelIdEngineMetricsGetResponse200 = {
  data: ModelEngineMetricsResponse;
  status: 200;
};

export type getModelEngineMetricsApiModelsModelIdEngineMetricsGetResponse422 = {
  data: HTTPValidationError;
  status: 422;
};

export type getModelEngineMetricsApiModelsModelIdEngineMetricsGetResponseSuccess =
  getModelEngineMetricsApiModelsModelIdEngineMetricsGetResponse200 & {
    headers: Headers;
  };
export type getModelEngineMetricsApiModelsModelIdEngineMetricsGetResponseError =
  getModelEngineMetricsApiModelsModelIdEngineMetricsGetResponse422 & {
    headers: Headers;
  };

export type getModelEngineMetricsApiModelsModelIdEngineMetricsGetResponse =
  | getModelEngineMetricsApiModelsModelIdEngineMetricsGetResponseSuccess
  | getModelEngineMetricsApiModelsModelIdEngineMetricsGetResponseError;

export const getGetModelEngineMetricsApiModelsModelIdEngineMetricsGetUrl = (
  modelId: string,
  params?: GetModelEngineMetricsApiModelsModelIdEngineMetricsGetParams,
) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {
    if (value !== undefined) {
      normalizedParams.append(key, value === null ? "null" : String(value));
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0
    ? `/api/models/${modelId}/engine-metrics?${stringifiedParams}`
    : `/api/models/${modelId}/engine-metrics`;
};

/**
 * vLLM engine history for a dedicated-fleet model (TRA-1484): GPU and
 * KV cache usage, requests running and waiting, and speculative decoding,
 * as the gateway sampled them from Baseten's metrics export. The gateway
 * picks the bucket width and does the averaging; nothing is computed here.
 * @summary Get Model Engine Metrics
 */
export const getModelEngineMetricsApiModelsModelIdEngineMetricsGet = async (
  modelId: string,
  params?: GetModelEngineMetricsApiModelsModelIdEngineMetricsGetParams,
  options?: Parameters<typeof orvalFetch>[1],
): Promise<getModelEngineMetricsApiModelsModelIdEngineMetricsGetResponse> => {
  return orvalFetch<getModelEngineMetricsApiModelsModelIdEngineMetricsGetResponse>(
    getGetModelEngineMetricsApiModelsModelIdEngineMetricsGetUrl(
      modelId,
      params,
    ),
    {
      ...options,
      method: "GET",
    },
  );
};

export const getGetModelEngineMetricsApiModelsModelIdEngineMetricsGetQueryKey =
  (
    modelId: string,
    params?: GetModelEngineMetricsApiModelsModelIdEngineMetricsGetParams,
  ) => {
    return [
      `/api/models/${modelId}/engine-metrics`,
      ...(params ? [params] : []),
    ] as const;
  };

export const getGetModelEngineMetricsApiModelsModelIdEngineMetricsGetQueryOptions =
  <
    TData = Awaited<
      ReturnType<typeof getModelEngineMetricsApiModelsModelIdEngineMetricsGet>
    >,
    TError = ErrorType<HTTPValidationError>,
  >(
    modelId: string,
    params?: GetModelEngineMetricsApiModelsModelIdEngineMetricsGetParams,
    options?: {
      query?: Partial<
        UseQueryOptions<
          Awaited<
            ReturnType<
              typeof getModelEngineMetricsApiModelsModelIdEngineMetricsGet
            >
          >,
          TError,
          TData
        >
      >;
      request?: SecondParameter<typeof orvalFetch>;
    },
  ) => {
    const { query: queryOptions, request: requestOptions } = options ?? {};

    const queryKey =
      queryOptions?.queryKey ??
      getGetModelEngineMetricsApiModelsModelIdEngineMetricsGetQueryKey(
        modelId,
        params,
      );

    const queryFn: QueryFunction<
      Awaited<
        ReturnType<typeof getModelEngineMetricsApiModelsModelIdEngineMetricsGet>
      >
    > = ({ signal }) =>
      getModelEngineMetricsApiModelsModelIdEngineMetricsGet(modelId, params, {
        signal,
        ...requestOptions,
      });

    return {
      queryKey,
      queryFn,
      enabled: modelId !== null && modelId !== undefined,
      ...queryOptions,
    } as UseQueryOptions<
      Awaited<
        ReturnType<typeof getModelEngineMetricsApiModelsModelIdEngineMetricsGet>
      >,
      TError,
      TData
    > & { queryKey: DataTag<QueryKey, TData, TError> };
  };

export type GetModelEngineMetricsApiModelsModelIdEngineMetricsGetQueryResult =
  NonNullable<
    Awaited<
      ReturnType<typeof getModelEngineMetricsApiModelsModelIdEngineMetricsGet>
    >
  >;
export type GetModelEngineMetricsApiModelsModelIdEngineMetricsGetQueryError =
  ErrorType<HTTPValidationError>;

export function useGetModelEngineMetricsApiModelsModelIdEngineMetricsGet<
  TData = Awaited<
    ReturnType<typeof getModelEngineMetricsApiModelsModelIdEngineMetricsGet>
  >,
  TError = ErrorType<HTTPValidationError>,
>(
  modelId: string,
  params:
    | undefined
    | GetModelEngineMetricsApiModelsModelIdEngineMetricsGetParams,
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<
            typeof getModelEngineMetricsApiModelsModelIdEngineMetricsGet
          >
        >,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<
            ReturnType<
              typeof getModelEngineMetricsApiModelsModelIdEngineMetricsGet
            >
          >,
          TError,
          Awaited<
            ReturnType<
              typeof getModelEngineMetricsApiModelsModelIdEngineMetricsGet
            >
          >
        >,
        "initialData"
      >;
    request?: SecondParameter<typeof orvalFetch>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetModelEngineMetricsApiModelsModelIdEngineMetricsGet<
  TData = Awaited<
    ReturnType<typeof getModelEngineMetricsApiModelsModelIdEngineMetricsGet>
  >,
  TError = ErrorType<HTTPValidationError>,
>(
  modelId: string,
  params?: GetModelEngineMetricsApiModelsModelIdEngineMetricsGetParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<
            typeof getModelEngineMetricsApiModelsModelIdEngineMetricsGet
          >
        >,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<
            ReturnType<
              typeof getModelEngineMetricsApiModelsModelIdEngineMetricsGet
            >
          >,
          TError,
          Awaited<
            ReturnType<
              typeof getModelEngineMetricsApiModelsModelIdEngineMetricsGet
            >
          >
        >,
        "initialData"
      >;
    request?: SecondParameter<typeof orvalFetch>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetModelEngineMetricsApiModelsModelIdEngineMetricsGet<
  TData = Awaited<
    ReturnType<typeof getModelEngineMetricsApiModelsModelIdEngineMetricsGet>
  >,
  TError = ErrorType<HTTPValidationError>,
>(
  modelId: string,
  params?: GetModelEngineMetricsApiModelsModelIdEngineMetricsGetParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<
            typeof getModelEngineMetricsApiModelsModelIdEngineMetricsGet
          >
        >,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof orvalFetch>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
/**
 * @summary Get Model Engine Metrics
 */

export function useGetModelEngineMetricsApiModelsModelIdEngineMetricsGet<
  TData = Awaited<
    ReturnType<typeof getModelEngineMetricsApiModelsModelIdEngineMetricsGet>
  >,
  TError = ErrorType<HTTPValidationError>,
>(
  modelId: string,
  params?: GetModelEngineMetricsApiModelsModelIdEngineMetricsGetParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<
            typeof getModelEngineMetricsApiModelsModelIdEngineMetricsGet
          >
        >,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof orvalFetch>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions =
    getGetModelEngineMetricsApiModelsModelIdEngineMetricsGetQueryOptions(
      modelId,
      params,
      options,
    );

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}

export type wakeModelApiModelsModelIdScalingWakePostResponse200 = {
  data: ModelScalingResponse;
  status: 200;
};

export type wakeModelApiModelsModelIdScalingWakePostResponse422 = {
  data: HTTPValidationError;
  status: 422;
};

export type wakeModelApiModelsModelIdScalingWakePostResponseSuccess =
  wakeModelApiModelsModelIdScalingWakePostResponse200 & {
    headers: Headers;
  };
export type wakeModelApiModelsModelIdScalingWakePostResponseError =
  wakeModelApiModelsModelIdScalingWakePostResponse422 & {
    headers: Headers;
  };

export type wakeModelApiModelsModelIdScalingWakePostResponse =
  | wakeModelApiModelsModelIdScalingWakePostResponseSuccess
  | wakeModelApiModelsModelIdScalingWakePostResponseError;

export const getWakeModelApiModelsModelIdScalingWakePostUrl = (
  modelId: string,
) => {
  return `/api/models/${modelId}/scaling/wake`;
};

/**
 * Wake a scaled-to-zero Baseten deployment so the cold start happens
 * now instead of on the first request. Returns the post-wake state; the
 * replica count catches up over the next minutes as the pod boots.
 * @summary Wake Model
 */
export const wakeModelApiModelsModelIdScalingWakePost = async (
  modelId: string,
  options?: Parameters<typeof orvalFetch>[1],
): Promise<wakeModelApiModelsModelIdScalingWakePostResponse> => {
  return orvalFetch<wakeModelApiModelsModelIdScalingWakePostResponse>(
    getWakeModelApiModelsModelIdScalingWakePostUrl(modelId),
    {
      ...options,
      method: "POST",
    },
  );
};

export const getWakeModelApiModelsModelIdScalingWakePostMutationKey = () =>
  ["wakeModelApiModelsModelIdScalingWakePost"] as const;

export const getWakeModelApiModelsModelIdScalingWakePostMutationOptions = <
  TError = ErrorType<HTTPValidationError>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof wakeModelApiModelsModelIdScalingWakePost>>,
    TError,
    WakeModelApiModelsModelIdScalingWakePostMutationVariables,
    TContext
  >;
  request?: SecondParameter<typeof orvalFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof wakeModelApiModelsModelIdScalingWakePost>>,
  TError,
  WakeModelApiModelsModelIdScalingWakePostMutationVariables,
  TContext
> => {
  const mutationKey = getWakeModelApiModelsModelIdScalingWakePostMutationKey();
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof wakeModelApiModelsModelIdScalingWakePost>>,
    WakeModelApiModelsModelIdScalingWakePostMutationVariables
  > = (props) => {
    const { modelId } = props ?? {};

    return wakeModelApiModelsModelIdScalingWakePost(modelId, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type WakeModelApiModelsModelIdScalingWakePostMutationResult =
  NonNullable<
    Awaited<ReturnType<typeof wakeModelApiModelsModelIdScalingWakePost>>
  >;

export type WakeModelApiModelsModelIdScalingWakePostMutationError =
  ErrorType<HTTPValidationError>;
export type WakeModelApiModelsModelIdScalingWakePostMutationVariables = {
  modelId: string;
};

/**
 * @summary Wake Model
 */
export const useWakeModelApiModelsModelIdScalingWakePost = <
  TError = ErrorType<HTTPValidationError>,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof wakeModelApiModelsModelIdScalingWakePost>>,
      TError,
      WakeModelApiModelsModelIdScalingWakePostMutationVariables,
      TContext
    >;
    request?: SecondParameter<typeof orvalFetch>;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof wakeModelApiModelsModelIdScalingWakePost>>,
  TError,
  WakeModelApiModelsModelIdScalingWakePostMutationVariables,
  TContext
> => {
  return useMutation(
    getWakeModelApiModelsModelIdScalingWakePostMutationOptions(options),
    queryClient,
  );
};
export type getModelSchedulesApiModelsModelIdScalingSchedulesGetResponse200 = {
  data: ModelSchedulesResponse;
  status: 200;
};

export type getModelSchedulesApiModelsModelIdScalingSchedulesGetResponse422 = {
  data: HTTPValidationError;
  status: 422;
};

export type getModelSchedulesApiModelsModelIdScalingSchedulesGetResponseSuccess =
  getModelSchedulesApiModelsModelIdScalingSchedulesGetResponse200 & {
    headers: Headers;
  };
export type getModelSchedulesApiModelsModelIdScalingSchedulesGetResponseError =
  getModelSchedulesApiModelsModelIdScalingSchedulesGetResponse422 & {
    headers: Headers;
  };

export type getModelSchedulesApiModelsModelIdScalingSchedulesGetResponse =
  | getModelSchedulesApiModelsModelIdScalingSchedulesGetResponseSuccess
  | getModelSchedulesApiModelsModelIdScalingSchedulesGetResponseError;

export const getGetModelSchedulesApiModelsModelIdScalingSchedulesGetUrl = (
  modelId: string,
) => {
  return `/api/models/${modelId}/scaling/schedules`;
};

/**
 * The autoscaling schedule windows of a dedicated Baseten model
 * (TRA-1426). They live on the Baseten environment the model serves from
 * and are read live through the gateway, which holds the key.
 * @summary Get Model Schedules
 */
export const getModelSchedulesApiModelsModelIdScalingSchedulesGet = async (
  modelId: string,
  options?: Parameters<typeof orvalFetch>[1],
): Promise<getModelSchedulesApiModelsModelIdScalingSchedulesGetResponse> => {
  return orvalFetch<getModelSchedulesApiModelsModelIdScalingSchedulesGetResponse>(
    getGetModelSchedulesApiModelsModelIdScalingSchedulesGetUrl(modelId),
    {
      ...options,
      method: "GET",
    },
  );
};

export const getGetModelSchedulesApiModelsModelIdScalingSchedulesGetQueryKey = (
  modelId: string,
) => {
  return [`/api/models/${modelId}/scaling/schedules`] as const;
};

export const getGetModelSchedulesApiModelsModelIdScalingSchedulesGetQueryOptions =
  <
    TData = Awaited<
      ReturnType<typeof getModelSchedulesApiModelsModelIdScalingSchedulesGet>
    >,
    TError = ErrorType<HTTPValidationError>,
  >(
    modelId: string,
    options?: {
      query?: Partial<
        UseQueryOptions<
          Awaited<
            ReturnType<
              typeof getModelSchedulesApiModelsModelIdScalingSchedulesGet
            >
          >,
          TError,
          TData
        >
      >;
      request?: SecondParameter<typeof orvalFetch>;
    },
  ) => {
    const { query: queryOptions, request: requestOptions } = options ?? {};

    const queryKey =
      queryOptions?.queryKey ??
      getGetModelSchedulesApiModelsModelIdScalingSchedulesGetQueryKey(modelId);

    const queryFn: QueryFunction<
      Awaited<
        ReturnType<typeof getModelSchedulesApiModelsModelIdScalingSchedulesGet>
      >
    > = ({ signal }) =>
      getModelSchedulesApiModelsModelIdScalingSchedulesGet(modelId, {
        signal,
        ...requestOptions,
      });

    return {
      queryKey,
      queryFn,
      enabled: modelId !== null && modelId !== undefined,
      ...queryOptions,
    } as UseQueryOptions<
      Awaited<
        ReturnType<typeof getModelSchedulesApiModelsModelIdScalingSchedulesGet>
      >,
      TError,
      TData
    > & { queryKey: DataTag<QueryKey, TData, TError> };
  };

export type GetModelSchedulesApiModelsModelIdScalingSchedulesGetQueryResult =
  NonNullable<
    Awaited<
      ReturnType<typeof getModelSchedulesApiModelsModelIdScalingSchedulesGet>
    >
  >;
export type GetModelSchedulesApiModelsModelIdScalingSchedulesGetQueryError =
  ErrorType<HTTPValidationError>;

export function useGetModelSchedulesApiModelsModelIdScalingSchedulesGet<
  TData = Awaited<
    ReturnType<typeof getModelSchedulesApiModelsModelIdScalingSchedulesGet>
  >,
  TError = ErrorType<HTTPValidationError>,
>(
  modelId: string,
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<
            typeof getModelSchedulesApiModelsModelIdScalingSchedulesGet
          >
        >,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<
            ReturnType<
              typeof getModelSchedulesApiModelsModelIdScalingSchedulesGet
            >
          >,
          TError,
          Awaited<
            ReturnType<
              typeof getModelSchedulesApiModelsModelIdScalingSchedulesGet
            >
          >
        >,
        "initialData"
      >;
    request?: SecondParameter<typeof orvalFetch>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetModelSchedulesApiModelsModelIdScalingSchedulesGet<
  TData = Awaited<
    ReturnType<typeof getModelSchedulesApiModelsModelIdScalingSchedulesGet>
  >,
  TError = ErrorType<HTTPValidationError>,
>(
  modelId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<
            typeof getModelSchedulesApiModelsModelIdScalingSchedulesGet
          >
        >,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<
            ReturnType<
              typeof getModelSchedulesApiModelsModelIdScalingSchedulesGet
            >
          >,
          TError,
          Awaited<
            ReturnType<
              typeof getModelSchedulesApiModelsModelIdScalingSchedulesGet
            >
          >
        >,
        "initialData"
      >;
    request?: SecondParameter<typeof orvalFetch>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetModelSchedulesApiModelsModelIdScalingSchedulesGet<
  TData = Awaited<
    ReturnType<typeof getModelSchedulesApiModelsModelIdScalingSchedulesGet>
  >,
  TError = ErrorType<HTTPValidationError>,
>(
  modelId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<
            typeof getModelSchedulesApiModelsModelIdScalingSchedulesGet
          >
        >,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof orvalFetch>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
/**
 * @summary Get Model Schedules
 */

export function useGetModelSchedulesApiModelsModelIdScalingSchedulesGet<
  TData = Awaited<
    ReturnType<typeof getModelSchedulesApiModelsModelIdScalingSchedulesGet>
  >,
  TError = ErrorType<HTTPValidationError>,
>(
  modelId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<
            typeof getModelSchedulesApiModelsModelIdScalingSchedulesGet
          >
        >,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof orvalFetch>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions =
    getGetModelSchedulesApiModelsModelIdScalingSchedulesGetQueryOptions(
      modelId,
      options,
    );

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}

export type updateModelSchedulesApiModelsModelIdScalingSchedulesPutResponse200 =
  {
    data: ModelSchedulesResponse;
    status: 200;
  };

export type updateModelSchedulesApiModelsModelIdScalingSchedulesPutResponse422 =
  {
    data: HTTPValidationError;
    status: 422;
  };

export type updateModelSchedulesApiModelsModelIdScalingSchedulesPutResponseSuccess =
  updateModelSchedulesApiModelsModelIdScalingSchedulesPutResponse200 & {
    headers: Headers;
  };
export type updateModelSchedulesApiModelsModelIdScalingSchedulesPutResponseError =
  updateModelSchedulesApiModelsModelIdScalingSchedulesPutResponse422 & {
    headers: Headers;
  };

export type updateModelSchedulesApiModelsModelIdScalingSchedulesPutResponse =
  | updateModelSchedulesApiModelsModelIdScalingSchedulesPutResponseSuccess
  | updateModelSchedulesApiModelsModelIdScalingSchedulesPutResponseError;

export const getUpdateModelSchedulesApiModelsModelIdScalingSchedulesPutUrl = (
  modelId: string,
) => {
  return `/api/models/${modelId}/scaling/schedules`;
};

/**
 * Set the weekday schedule windows of a dedicated Baseten model.
 *
 * The body is the full list to keep: a window with an id is replaced, one
 * without is created, one left out is deleted. It carries the revision of
 * the read it was built on, and the gateway answers 409 when the schedule
 * has changed since. Writes through the gateway, then re-reads so the
 * response carries the ids of created windows and the new revision.
 * Restricted demo teams are rejected by get_current_team_id before this
 * runs. The windows read before the write, the re-read and the person
 * saving go into the replica setting history (TRA-1558) once the gateway
 * has taken the write; a 409 records nothing. When the re-read fails the
 * save answers with that failure, and the row holds the requested
 * windows instead: Baseten has them either way.
 * @summary Update Model Schedules
 */
export const updateModelSchedulesApiModelsModelIdScalingSchedulesPut = async (
  modelId: string,
  modelSchedulesUpdate: ModelSchedulesUpdate,
  options?: Parameters<typeof orvalFetch>[1],
): Promise<updateModelSchedulesApiModelsModelIdScalingSchedulesPutResponse> => {
  const getHeaders = (
    h?: NonNullable<RequestInit["headers"]>,
  ): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(
          h as Iterable<Iterable<string>>,
          (entry) => Array.from(entry) as [string, string],
        ),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<
      string | readonly string[] | undefined
    >(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
  return orvalFetch<updateModelSchedulesApiModelsModelIdScalingSchedulesPutResponse>(
    getUpdateModelSchedulesApiModelsModelIdScalingSchedulesPutUrl(modelId),
    {
      ...options,
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        ...getHeaders(options?.headers),
      },
      body: JSON.stringify(modelSchedulesUpdate),
    },
  );
};

export const getUpdateModelSchedulesApiModelsModelIdScalingSchedulesPutMutationKey =
  () => ["updateModelSchedulesApiModelsModelIdScalingSchedulesPut"] as const;

export const getUpdateModelSchedulesApiModelsModelIdScalingSchedulesPutMutationOptions =
  <TError = ErrorType<HTTPValidationError>, TContext = unknown>(options?: {
    mutation?: UseMutationOptions<
      Awaited<
        ReturnType<
          typeof updateModelSchedulesApiModelsModelIdScalingSchedulesPut
        >
      >,
      TError,
      UpdateModelSchedulesApiModelsModelIdScalingSchedulesPutMutationVariables,
      TContext
    >;
    request?: SecondParameter<typeof orvalFetch>;
  }): UseMutationOptions<
    Awaited<
      ReturnType<typeof updateModelSchedulesApiModelsModelIdScalingSchedulesPut>
    >,
    TError,
    UpdateModelSchedulesApiModelsModelIdScalingSchedulesPutMutationVariables,
    TContext
  > => {
    const mutationKey =
      getUpdateModelSchedulesApiModelsModelIdScalingSchedulesPutMutationKey();
    const { mutation: mutationOptions, request: requestOptions } = options
      ? options.mutation &&
        "mutationKey" in options.mutation &&
        options.mutation.mutationKey
        ? options
        : { ...options, mutation: { ...options.mutation, mutationKey } }
      : { mutation: { mutationKey }, request: undefined };

    const mutationFn: MutationFunction<
      Awaited<
        ReturnType<
          typeof updateModelSchedulesApiModelsModelIdScalingSchedulesPut
        >
      >,
      UpdateModelSchedulesApiModelsModelIdScalingSchedulesPutMutationVariables
    > = (props) => {
      const { modelId, data } = props ?? {};

      return updateModelSchedulesApiModelsModelIdScalingSchedulesPut(
        modelId,
        data,
        requestOptions,
      );
    };

    return { mutationFn, ...mutationOptions };
  };

export type UpdateModelSchedulesApiModelsModelIdScalingSchedulesPutMutationResult =
  NonNullable<
    Awaited<
      ReturnType<typeof updateModelSchedulesApiModelsModelIdScalingSchedulesPut>
    >
  >;
export type UpdateModelSchedulesApiModelsModelIdScalingSchedulesPutMutationBody =
  ModelSchedulesUpdate;
export type UpdateModelSchedulesApiModelsModelIdScalingSchedulesPutMutationError =
  ErrorType<HTTPValidationError>;
export type UpdateModelSchedulesApiModelsModelIdScalingSchedulesPutMutationVariables =
  { modelId: string; data: ModelSchedulesUpdate };

/**
 * @summary Update Model Schedules
 */
export const useUpdateModelSchedulesApiModelsModelIdScalingSchedulesPut = <
  TError = ErrorType<HTTPValidationError>,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<
        ReturnType<
          typeof updateModelSchedulesApiModelsModelIdScalingSchedulesPut
        >
      >,
      TError,
      UpdateModelSchedulesApiModelsModelIdScalingSchedulesPutMutationVariables,
      TContext
    >;
    request?: SecondParameter<typeof orvalFetch>;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<
    ReturnType<typeof updateModelSchedulesApiModelsModelIdScalingSchedulesPut>
  >,
  TError,
  UpdateModelSchedulesApiModelsModelIdScalingSchedulesPutMutationVariables,
  TContext
> => {
  return useMutation(
    getUpdateModelSchedulesApiModelsModelIdScalingSchedulesPutMutationOptions(
      options,
    ),
    queryClient,
  );
};
export type getModelScalingChangesApiModelsModelIdScalingChangesGetResponse200 =
  {
    data: ReplicaSettingChangeResponse[];
    status: 200;
  };

export type getModelScalingChangesApiModelsModelIdScalingChangesGetResponse422 =
  {
    data: HTTPValidationError;
    status: 422;
  };

export type getModelScalingChangesApiModelsModelIdScalingChangesGetResponseSuccess =
  getModelScalingChangesApiModelsModelIdScalingChangesGetResponse200 & {
    headers: Headers;
  };
export type getModelScalingChangesApiModelsModelIdScalingChangesGetResponseError =
  getModelScalingChangesApiModelsModelIdScalingChangesGetResponse422 & {
    headers: Headers;
  };

export type getModelScalingChangesApiModelsModelIdScalingChangesGetResponse =
  | getModelScalingChangesApiModelsModelIdScalingChangesGetResponseSuccess
  | getModelScalingChangesApiModelsModelIdScalingChangesGetResponseError;

export const getGetModelScalingChangesApiModelsModelIdScalingChangesGetUrl = (
  modelId: string,
  params?: GetModelScalingChangesApiModelsModelIdScalingChangesGetParams,
) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {
    if (value !== undefined) {
      normalizedParams.append(key, value === null ? "null" : String(value));
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0
    ? `/api/models/${modelId}/scaling/changes?${stringifiedParams}`
    : `/api/models/${modelId}/scaling/changes`;
};

/**
 * The latest saves of this model's replica settings (TRA-1558), newest
 * first: who changed the regular range or the windows, when, and what
 * changed in one line. Another team's model is a 404, like every other
 * model route.
 * @summary Get Model Scaling Changes
 */
export const getModelScalingChangesApiModelsModelIdScalingChangesGet = async (
  modelId: string,
  params?: GetModelScalingChangesApiModelsModelIdScalingChangesGetParams,
  options?: Parameters<typeof orvalFetch>[1],
): Promise<getModelScalingChangesApiModelsModelIdScalingChangesGetResponse> => {
  return orvalFetch<getModelScalingChangesApiModelsModelIdScalingChangesGetResponse>(
    getGetModelScalingChangesApiModelsModelIdScalingChangesGetUrl(
      modelId,
      params,
    ),
    {
      ...options,
      method: "GET",
    },
  );
};

export const getGetModelScalingChangesApiModelsModelIdScalingChangesGetQueryKey =
  (
    modelId: string,
    params?: GetModelScalingChangesApiModelsModelIdScalingChangesGetParams,
  ) => {
    return [
      `/api/models/${modelId}/scaling/changes`,
      ...(params ? [params] : []),
    ] as const;
  };

export const getGetModelScalingChangesApiModelsModelIdScalingChangesGetQueryOptions =
  <
    TData = Awaited<
      ReturnType<typeof getModelScalingChangesApiModelsModelIdScalingChangesGet>
    >,
    TError = ErrorType<HTTPValidationError>,
  >(
    modelId: string,
    params?: GetModelScalingChangesApiModelsModelIdScalingChangesGetParams,
    options?: {
      query?: Partial<
        UseQueryOptions<
          Awaited<
            ReturnType<
              typeof getModelScalingChangesApiModelsModelIdScalingChangesGet
            >
          >,
          TError,
          TData
        >
      >;
      request?: SecondParameter<typeof orvalFetch>;
    },
  ) => {
    const { query: queryOptions, request: requestOptions } = options ?? {};

    const queryKey =
      queryOptions?.queryKey ??
      getGetModelScalingChangesApiModelsModelIdScalingChangesGetQueryKey(
        modelId,
        params,
      );

    const queryFn: QueryFunction<
      Awaited<
        ReturnType<
          typeof getModelScalingChangesApiModelsModelIdScalingChangesGet
        >
      >
    > = ({ signal }) =>
      getModelScalingChangesApiModelsModelIdScalingChangesGet(modelId, params, {
        signal,
        ...requestOptions,
      });

    return {
      queryKey,
      queryFn,
      enabled: modelId !== null && modelId !== undefined,
      ...queryOptions,
    } as UseQueryOptions<
      Awaited<
        ReturnType<
          typeof getModelScalingChangesApiModelsModelIdScalingChangesGet
        >
      >,
      TError,
      TData
    > & { queryKey: DataTag<QueryKey, TData, TError> };
  };

export type GetModelScalingChangesApiModelsModelIdScalingChangesGetQueryResult =
  NonNullable<
    Awaited<
      ReturnType<typeof getModelScalingChangesApiModelsModelIdScalingChangesGet>
    >
  >;
export type GetModelScalingChangesApiModelsModelIdScalingChangesGetQueryError =
  ErrorType<HTTPValidationError>;

export function useGetModelScalingChangesApiModelsModelIdScalingChangesGet<
  TData = Awaited<
    ReturnType<typeof getModelScalingChangesApiModelsModelIdScalingChangesGet>
  >,
  TError = ErrorType<HTTPValidationError>,
>(
  modelId: string,
  params:
    | undefined
    | GetModelScalingChangesApiModelsModelIdScalingChangesGetParams,
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<
            typeof getModelScalingChangesApiModelsModelIdScalingChangesGet
          >
        >,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<
            ReturnType<
              typeof getModelScalingChangesApiModelsModelIdScalingChangesGet
            >
          >,
          TError,
          Awaited<
            ReturnType<
              typeof getModelScalingChangesApiModelsModelIdScalingChangesGet
            >
          >
        >,
        "initialData"
      >;
    request?: SecondParameter<typeof orvalFetch>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetModelScalingChangesApiModelsModelIdScalingChangesGet<
  TData = Awaited<
    ReturnType<typeof getModelScalingChangesApiModelsModelIdScalingChangesGet>
  >,
  TError = ErrorType<HTTPValidationError>,
>(
  modelId: string,
  params?: GetModelScalingChangesApiModelsModelIdScalingChangesGetParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<
            typeof getModelScalingChangesApiModelsModelIdScalingChangesGet
          >
        >,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<
            ReturnType<
              typeof getModelScalingChangesApiModelsModelIdScalingChangesGet
            >
          >,
          TError,
          Awaited<
            ReturnType<
              typeof getModelScalingChangesApiModelsModelIdScalingChangesGet
            >
          >
        >,
        "initialData"
      >;
    request?: SecondParameter<typeof orvalFetch>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetModelScalingChangesApiModelsModelIdScalingChangesGet<
  TData = Awaited<
    ReturnType<typeof getModelScalingChangesApiModelsModelIdScalingChangesGet>
  >,
  TError = ErrorType<HTTPValidationError>,
>(
  modelId: string,
  params?: GetModelScalingChangesApiModelsModelIdScalingChangesGetParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<
            typeof getModelScalingChangesApiModelsModelIdScalingChangesGet
          >
        >,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof orvalFetch>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
/**
 * @summary Get Model Scaling Changes
 */

export function useGetModelScalingChangesApiModelsModelIdScalingChangesGet<
  TData = Awaited<
    ReturnType<typeof getModelScalingChangesApiModelsModelIdScalingChangesGet>
  >,
  TError = ErrorType<HTTPValidationError>,
>(
  modelId: string,
  params?: GetModelScalingChangesApiModelsModelIdScalingChangesGetParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<
            typeof getModelScalingChangesApiModelsModelIdScalingChangesGet
          >
        >,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof orvalFetch>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions =
    getGetModelScalingChangesApiModelsModelIdScalingChangesGetQueryOptions(
      modelId,
      params,
      options,
    );

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}

export type getModelScalingUsageApiModelsModelIdScalingUsageGetResponse200 = {
  data: ModelScalingUsageResponse;
  status: 200;
};

export type getModelScalingUsageApiModelsModelIdScalingUsageGetResponse422 = {
  data: HTTPValidationError;
  status: 422;
};

export type getModelScalingUsageApiModelsModelIdScalingUsageGetResponseSuccess =
  getModelScalingUsageApiModelsModelIdScalingUsageGetResponse200 & {
    headers: Headers;
  };
export type getModelScalingUsageApiModelsModelIdScalingUsageGetResponseError =
  getModelScalingUsageApiModelsModelIdScalingUsageGetResponse422 & {
    headers: Headers;
  };

export type getModelScalingUsageApiModelsModelIdScalingUsageGetResponse =
  | getModelScalingUsageApiModelsModelIdScalingUsageGetResponseSuccess
  | getModelScalingUsageApiModelsModelIdScalingUsageGetResponseError;

export const getGetModelScalingUsageApiModelsModelIdScalingUsageGetUrl = (
  modelId: string,
  params?: GetModelScalingUsageApiModelsModelIdScalingUsageGetParams,
) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {
    if (value !== undefined) {
      normalizedParams.append(key, value === null ? "null" : String(value));
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0
    ? `/api/models/${modelId}/scaling/usage?${stringifiedParams}`
    : `/api/models/${modelId}/scaling/usage`;
};

/**
 * How much of the running replicas the traffic used, for a dedicated
 * Baseten model (TRA-1437), plus a summary per schedule window over the
 * same hours. Four gateway reads feed one pure computation
 * (`services/replica_usage.py`): the autoscaling knobs and replica
 * samples, the schedule windows, the model's usage rollups, and the
 * engine metrics with Baseten's own count of requests running. The last
 * one is best effort: without it every step is the rollups' estimate.
 * @summary Get Model Scaling Usage
 */
export const getModelScalingUsageApiModelsModelIdScalingUsageGet = async (
  modelId: string,
  params?: GetModelScalingUsageApiModelsModelIdScalingUsageGetParams,
  options?: Parameters<typeof orvalFetch>[1],
): Promise<getModelScalingUsageApiModelsModelIdScalingUsageGetResponse> => {
  return orvalFetch<getModelScalingUsageApiModelsModelIdScalingUsageGetResponse>(
    getGetModelScalingUsageApiModelsModelIdScalingUsageGetUrl(modelId, params),
    {
      ...options,
      method: "GET",
    },
  );
};

export const getGetModelScalingUsageApiModelsModelIdScalingUsageGetQueryKey = (
  modelId: string,
  params?: GetModelScalingUsageApiModelsModelIdScalingUsageGetParams,
) => {
  return [
    `/api/models/${modelId}/scaling/usage`,
    ...(params ? [params] : []),
  ] as const;
};

export const getGetModelScalingUsageApiModelsModelIdScalingUsageGetQueryOptions =
  <
    TData = Awaited<
      ReturnType<typeof getModelScalingUsageApiModelsModelIdScalingUsageGet>
    >,
    TError = ErrorType<HTTPValidationError>,
  >(
    modelId: string,
    params?: GetModelScalingUsageApiModelsModelIdScalingUsageGetParams,
    options?: {
      query?: Partial<
        UseQueryOptions<
          Awaited<
            ReturnType<
              typeof getModelScalingUsageApiModelsModelIdScalingUsageGet
            >
          >,
          TError,
          TData
        >
      >;
      request?: SecondParameter<typeof orvalFetch>;
    },
  ) => {
    const { query: queryOptions, request: requestOptions } = options ?? {};

    const queryKey =
      queryOptions?.queryKey ??
      getGetModelScalingUsageApiModelsModelIdScalingUsageGetQueryKey(
        modelId,
        params,
      );

    const queryFn: QueryFunction<
      Awaited<
        ReturnType<typeof getModelScalingUsageApiModelsModelIdScalingUsageGet>
      >
    > = ({ signal }) =>
      getModelScalingUsageApiModelsModelIdScalingUsageGet(modelId, params, {
        signal,
        ...requestOptions,
      });

    return {
      queryKey,
      queryFn,
      enabled: modelId !== null && modelId !== undefined,
      ...queryOptions,
    } as UseQueryOptions<
      Awaited<
        ReturnType<typeof getModelScalingUsageApiModelsModelIdScalingUsageGet>
      >,
      TError,
      TData
    > & { queryKey: DataTag<QueryKey, TData, TError> };
  };

export type GetModelScalingUsageApiModelsModelIdScalingUsageGetQueryResult =
  NonNullable<
    Awaited<
      ReturnType<typeof getModelScalingUsageApiModelsModelIdScalingUsageGet>
    >
  >;
export type GetModelScalingUsageApiModelsModelIdScalingUsageGetQueryError =
  ErrorType<HTTPValidationError>;

export function useGetModelScalingUsageApiModelsModelIdScalingUsageGet<
  TData = Awaited<
    ReturnType<typeof getModelScalingUsageApiModelsModelIdScalingUsageGet>
  >,
  TError = ErrorType<HTTPValidationError>,
>(
  modelId: string,
  params: undefined | GetModelScalingUsageApiModelsModelIdScalingUsageGetParams,
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<typeof getModelScalingUsageApiModelsModelIdScalingUsageGet>
        >,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<
            ReturnType<
              typeof getModelScalingUsageApiModelsModelIdScalingUsageGet
            >
          >,
          TError,
          Awaited<
            ReturnType<
              typeof getModelScalingUsageApiModelsModelIdScalingUsageGet
            >
          >
        >,
        "initialData"
      >;
    request?: SecondParameter<typeof orvalFetch>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetModelScalingUsageApiModelsModelIdScalingUsageGet<
  TData = Awaited<
    ReturnType<typeof getModelScalingUsageApiModelsModelIdScalingUsageGet>
  >,
  TError = ErrorType<HTTPValidationError>,
>(
  modelId: string,
  params?: GetModelScalingUsageApiModelsModelIdScalingUsageGetParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<typeof getModelScalingUsageApiModelsModelIdScalingUsageGet>
        >,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<
            ReturnType<
              typeof getModelScalingUsageApiModelsModelIdScalingUsageGet
            >
          >,
          TError,
          Awaited<
            ReturnType<
              typeof getModelScalingUsageApiModelsModelIdScalingUsageGet
            >
          >
        >,
        "initialData"
      >;
    request?: SecondParameter<typeof orvalFetch>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetModelScalingUsageApiModelsModelIdScalingUsageGet<
  TData = Awaited<
    ReturnType<typeof getModelScalingUsageApiModelsModelIdScalingUsageGet>
  >,
  TError = ErrorType<HTTPValidationError>,
>(
  modelId: string,
  params?: GetModelScalingUsageApiModelsModelIdScalingUsageGetParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<typeof getModelScalingUsageApiModelsModelIdScalingUsageGet>
        >,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof orvalFetch>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
/**
 * @summary Get Model Scaling Usage
 */

export function useGetModelScalingUsageApiModelsModelIdScalingUsageGet<
  TData = Awaited<
    ReturnType<typeof getModelScalingUsageApiModelsModelIdScalingUsageGet>
  >,
  TError = ErrorType<HTTPValidationError>,
>(
  modelId: string,
  params?: GetModelScalingUsageApiModelsModelIdScalingUsageGetParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<typeof getModelScalingUsageApiModelsModelIdScalingUsageGet>
        >,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof orvalFetch>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions =
    getGetModelScalingUsageApiModelsModelIdScalingUsageGetQueryOptions(
      modelId,
      params,
      options,
    );

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}

export type listModelRequestsApiModelsModelIdRequestsGetResponse200 = {
  data: RequestLogResponse;
  status: 200;
};

export type listModelRequestsApiModelsModelIdRequestsGetResponse422 = {
  data: HTTPValidationError;
  status: 422;
};

export type listModelRequestsApiModelsModelIdRequestsGetResponseSuccess =
  listModelRequestsApiModelsModelIdRequestsGetResponse200 & {
    headers: Headers;
  };
export type listModelRequestsApiModelsModelIdRequestsGetResponseError =
  listModelRequestsApiModelsModelIdRequestsGetResponse422 & {
    headers: Headers;
  };

export type listModelRequestsApiModelsModelIdRequestsGetResponse =
  | listModelRequestsApiModelsModelIdRequestsGetResponseSuccess
  | listModelRequestsApiModelsModelIdRequestsGetResponseError;

export const getListModelRequestsApiModelsModelIdRequestsGetUrl = (
  modelId: string,
  params?: ListModelRequestsApiModelsModelIdRequestsGetParams,
) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {
    if (value !== undefined) {
      normalizedParams.append(key, value === null ? "null" : String(value));
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0
    ? `/api/models/${modelId}/requests?${stringifiedParams}`
    : `/api/models/${modelId}/requests`;
};

/**
 * List the gateway request log for a single model.
 *
 * Reads the gateway's always-on D1 audit log, scoped to this model's slug
 * (the gateway stores the client-sent model string, which equals our
 * `Model.slug`). Each row carries a `trace_key` when the team opted into
 * trace logging and a body was captured — that key deep-links to the full
 * request/response body in the trace-detail view.
 *
 * Returns an empty page (rather than 404) when the team has no gateway
 * credentials yet — a freshly created model may have no traffic.
 * Opening the log is recorded in the team's audit log (TRA-1626).
 * @summary List Model Requests
 */
export const listModelRequestsApiModelsModelIdRequestsGet = async (
  modelId: string,
  params?: ListModelRequestsApiModelsModelIdRequestsGetParams,
  options?: Parameters<typeof orvalFetch>[1],
): Promise<listModelRequestsApiModelsModelIdRequestsGetResponse> => {
  return orvalFetch<listModelRequestsApiModelsModelIdRequestsGetResponse>(
    getListModelRequestsApiModelsModelIdRequestsGetUrl(modelId, params),
    {
      ...options,
      method: "GET",
    },
  );
};

export const getListModelRequestsApiModelsModelIdRequestsGetQueryKey = (
  modelId: string,
  params?: ListModelRequestsApiModelsModelIdRequestsGetParams,
) => {
  return [
    `/api/models/${modelId}/requests`,
    ...(params ? [params] : []),
  ] as const;
};

export const getListModelRequestsApiModelsModelIdRequestsGetQueryOptions = <
  TData = Awaited<
    ReturnType<typeof listModelRequestsApiModelsModelIdRequestsGet>
  >,
  TError = ErrorType<HTTPValidationError>,
>(
  modelId: string,
  params?: ListModelRequestsApiModelsModelIdRequestsGetParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<typeof listModelRequestsApiModelsModelIdRequestsGet>
        >,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof orvalFetch>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getListModelRequestsApiModelsModelIdRequestsGetQueryKey(modelId, params);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof listModelRequestsApiModelsModelIdRequestsGet>>
  > = ({ signal }) =>
    listModelRequestsApiModelsModelIdRequestsGet(modelId, params, {
      signal,
      ...requestOptions,
    });

  return {
    queryKey,
    queryFn,
    enabled: modelId !== null && modelId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<
    Awaited<ReturnType<typeof listModelRequestsApiModelsModelIdRequestsGet>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type ListModelRequestsApiModelsModelIdRequestsGetQueryResult =
  NonNullable<
    Awaited<ReturnType<typeof listModelRequestsApiModelsModelIdRequestsGet>>
  >;
export type ListModelRequestsApiModelsModelIdRequestsGetQueryError =
  ErrorType<HTTPValidationError>;

export function useListModelRequestsApiModelsModelIdRequestsGet<
  TData = Awaited<
    ReturnType<typeof listModelRequestsApiModelsModelIdRequestsGet>
  >,
  TError = ErrorType<HTTPValidationError>,
>(
  modelId: string,
  params: undefined | ListModelRequestsApiModelsModelIdRequestsGetParams,
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<typeof listModelRequestsApiModelsModelIdRequestsGet>
        >,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<
            ReturnType<typeof listModelRequestsApiModelsModelIdRequestsGet>
          >,
          TError,
          Awaited<
            ReturnType<typeof listModelRequestsApiModelsModelIdRequestsGet>
          >
        >,
        "initialData"
      >;
    request?: SecondParameter<typeof orvalFetch>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useListModelRequestsApiModelsModelIdRequestsGet<
  TData = Awaited<
    ReturnType<typeof listModelRequestsApiModelsModelIdRequestsGet>
  >,
  TError = ErrorType<HTTPValidationError>,
>(
  modelId: string,
  params?: ListModelRequestsApiModelsModelIdRequestsGetParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<typeof listModelRequestsApiModelsModelIdRequestsGet>
        >,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<
            ReturnType<typeof listModelRequestsApiModelsModelIdRequestsGet>
          >,
          TError,
          Awaited<
            ReturnType<typeof listModelRequestsApiModelsModelIdRequestsGet>
          >
        >,
        "initialData"
      >;
    request?: SecondParameter<typeof orvalFetch>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useListModelRequestsApiModelsModelIdRequestsGet<
  TData = Awaited<
    ReturnType<typeof listModelRequestsApiModelsModelIdRequestsGet>
  >,
  TError = ErrorType<HTTPValidationError>,
>(
  modelId: string,
  params?: ListModelRequestsApiModelsModelIdRequestsGetParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<typeof listModelRequestsApiModelsModelIdRequestsGet>
        >,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof orvalFetch>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
/**
 * @summary List Model Requests
 */

export function useListModelRequestsApiModelsModelIdRequestsGet<
  TData = Awaited<
    ReturnType<typeof listModelRequestsApiModelsModelIdRequestsGet>
  >,
  TError = ErrorType<HTTPValidationError>,
>(
  modelId: string,
  params?: ListModelRequestsApiModelsModelIdRequestsGetParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<typeof listModelRequestsApiModelsModelIdRequestsGet>
        >,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof orvalFetch>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions =
    getListModelRequestsApiModelsModelIdRequestsGetQueryOptions(
      modelId,
      params,
      options,
    );

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}

export type getModelUsageSummaryApiModelsModelIdUsageGetResponse200 = {
  data: UsageSummaryResponse;
  status: 200;
};

export type getModelUsageSummaryApiModelsModelIdUsageGetResponse422 = {
  data: HTTPValidationError;
  status: 422;
};

export type getModelUsageSummaryApiModelsModelIdUsageGetResponseSuccess =
  getModelUsageSummaryApiModelsModelIdUsageGetResponse200 & {
    headers: Headers;
  };
export type getModelUsageSummaryApiModelsModelIdUsageGetResponseError =
  getModelUsageSummaryApiModelsModelIdUsageGetResponse422 & {
    headers: Headers;
  };

export type getModelUsageSummaryApiModelsModelIdUsageGetResponse =
  | getModelUsageSummaryApiModelsModelIdUsageGetResponseSuccess
  | getModelUsageSummaryApiModelsModelIdUsageGetResponseError;

export const getGetModelUsageSummaryApiModelsModelIdUsageGetUrl = (
  modelId: string,
  params?: GetModelUsageSummaryApiModelsModelIdUsageGetParams,
) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {
    if (value !== undefined) {
      normalizedParams.append(key, value === null ? "null" : String(value));
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0
    ? `/api/models/${modelId}/usage?${stringifiedParams}`
    : `/api/models/${modelId}/usage`;
};

/**
 * Aggregate usage totals for a single model over the given window.
 *
 * Reads the gateway's always-on D1 audit log, scoped to this model's slug.
 * Returns zeroes (rather than 404) when the team has no gateway credentials
 * yet — a freshly created model simply has no traffic.
 * @summary Get Model Usage Summary
 */
export const getModelUsageSummaryApiModelsModelIdUsageGet = async (
  modelId: string,
  params?: GetModelUsageSummaryApiModelsModelIdUsageGetParams,
  options?: Parameters<typeof orvalFetch>[1],
): Promise<getModelUsageSummaryApiModelsModelIdUsageGetResponse> => {
  return orvalFetch<getModelUsageSummaryApiModelsModelIdUsageGetResponse>(
    getGetModelUsageSummaryApiModelsModelIdUsageGetUrl(modelId, params),
    {
      ...options,
      method: "GET",
    },
  );
};

export const getGetModelUsageSummaryApiModelsModelIdUsageGetQueryKey = (
  modelId: string,
  params?: GetModelUsageSummaryApiModelsModelIdUsageGetParams,
) => {
  return [`/api/models/${modelId}/usage`, ...(params ? [params] : [])] as const;
};

export const getGetModelUsageSummaryApiModelsModelIdUsageGetQueryOptions = <
  TData = Awaited<
    ReturnType<typeof getModelUsageSummaryApiModelsModelIdUsageGet>
  >,
  TError = ErrorType<HTTPValidationError>,
>(
  modelId: string,
  params?: GetModelUsageSummaryApiModelsModelIdUsageGetParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<typeof getModelUsageSummaryApiModelsModelIdUsageGet>
        >,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof orvalFetch>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getGetModelUsageSummaryApiModelsModelIdUsageGetQueryKey(modelId, params);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getModelUsageSummaryApiModelsModelIdUsageGet>>
  > = ({ signal }) =>
    getModelUsageSummaryApiModelsModelIdUsageGet(modelId, params, {
      signal,
      ...requestOptions,
    });

  return {
    queryKey,
    queryFn,
    enabled: modelId !== null && modelId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<
    Awaited<ReturnType<typeof getModelUsageSummaryApiModelsModelIdUsageGet>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetModelUsageSummaryApiModelsModelIdUsageGetQueryResult =
  NonNullable<
    Awaited<ReturnType<typeof getModelUsageSummaryApiModelsModelIdUsageGet>>
  >;
export type GetModelUsageSummaryApiModelsModelIdUsageGetQueryError =
  ErrorType<HTTPValidationError>;

export function useGetModelUsageSummaryApiModelsModelIdUsageGet<
  TData = Awaited<
    ReturnType<typeof getModelUsageSummaryApiModelsModelIdUsageGet>
  >,
  TError = ErrorType<HTTPValidationError>,
>(
  modelId: string,
  params: undefined | GetModelUsageSummaryApiModelsModelIdUsageGetParams,
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<typeof getModelUsageSummaryApiModelsModelIdUsageGet>
        >,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<
            ReturnType<typeof getModelUsageSummaryApiModelsModelIdUsageGet>
          >,
          TError,
          Awaited<
            ReturnType<typeof getModelUsageSummaryApiModelsModelIdUsageGet>
          >
        >,
        "initialData"
      >;
    request?: SecondParameter<typeof orvalFetch>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetModelUsageSummaryApiModelsModelIdUsageGet<
  TData = Awaited<
    ReturnType<typeof getModelUsageSummaryApiModelsModelIdUsageGet>
  >,
  TError = ErrorType<HTTPValidationError>,
>(
  modelId: string,
  params?: GetModelUsageSummaryApiModelsModelIdUsageGetParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<typeof getModelUsageSummaryApiModelsModelIdUsageGet>
        >,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<
            ReturnType<typeof getModelUsageSummaryApiModelsModelIdUsageGet>
          >,
          TError,
          Awaited<
            ReturnType<typeof getModelUsageSummaryApiModelsModelIdUsageGet>
          >
        >,
        "initialData"
      >;
    request?: SecondParameter<typeof orvalFetch>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetModelUsageSummaryApiModelsModelIdUsageGet<
  TData = Awaited<
    ReturnType<typeof getModelUsageSummaryApiModelsModelIdUsageGet>
  >,
  TError = ErrorType<HTTPValidationError>,
>(
  modelId: string,
  params?: GetModelUsageSummaryApiModelsModelIdUsageGetParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<typeof getModelUsageSummaryApiModelsModelIdUsageGet>
        >,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof orvalFetch>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
/**
 * @summary Get Model Usage Summary
 */

export function useGetModelUsageSummaryApiModelsModelIdUsageGet<
  TData = Awaited<
    ReturnType<typeof getModelUsageSummaryApiModelsModelIdUsageGet>
  >,
  TError = ErrorType<HTTPValidationError>,
>(
  modelId: string,
  params?: GetModelUsageSummaryApiModelsModelIdUsageGetParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<typeof getModelUsageSummaryApiModelsModelIdUsageGet>
        >,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof orvalFetch>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions =
    getGetModelUsageSummaryApiModelsModelIdUsageGetQueryOptions(
      modelId,
      params,
      options,
    );

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}

export type getModelUsageDailyApiModelsModelIdUsageDailyGetResponse200 = {
  data: UsageDailyResponse;
  status: 200;
};

export type getModelUsageDailyApiModelsModelIdUsageDailyGetResponse422 = {
  data: HTTPValidationError;
  status: 422;
};

export type getModelUsageDailyApiModelsModelIdUsageDailyGetResponseSuccess =
  getModelUsageDailyApiModelsModelIdUsageDailyGetResponse200 & {
    headers: Headers;
  };
export type getModelUsageDailyApiModelsModelIdUsageDailyGetResponseError =
  getModelUsageDailyApiModelsModelIdUsageDailyGetResponse422 & {
    headers: Headers;
  };

export type getModelUsageDailyApiModelsModelIdUsageDailyGetResponse =
  | getModelUsageDailyApiModelsModelIdUsageDailyGetResponseSuccess
  | getModelUsageDailyApiModelsModelIdUsageDailyGetResponseError;

export const getGetModelUsageDailyApiModelsModelIdUsageDailyGetUrl = (
  modelId: string,
  params?: GetModelUsageDailyApiModelsModelIdUsageDailyGetParams,
) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {
    if (value !== undefined) {
      normalizedParams.append(key, value === null ? "null" : String(value));
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0
    ? `/api/models/${modelId}/usage/daily?${stringifiedParams}`
    : `/api/models/${modelId}/usage/daily`;
};

/**
 * Per-day usage breakdown for a single model — powers the overview chart.
 *
 * Days with no traffic are absent from the gateway's response; the frontend
 * is responsible for any gap-filling it wants for a contiguous axis.
 * @summary Get Model Usage Daily
 */
export const getModelUsageDailyApiModelsModelIdUsageDailyGet = async (
  modelId: string,
  params?: GetModelUsageDailyApiModelsModelIdUsageDailyGetParams,
  options?: Parameters<typeof orvalFetch>[1],
): Promise<getModelUsageDailyApiModelsModelIdUsageDailyGetResponse> => {
  return orvalFetch<getModelUsageDailyApiModelsModelIdUsageDailyGetResponse>(
    getGetModelUsageDailyApiModelsModelIdUsageDailyGetUrl(modelId, params),
    {
      ...options,
      method: "GET",
    },
  );
};

export const getGetModelUsageDailyApiModelsModelIdUsageDailyGetQueryKey = (
  modelId: string,
  params?: GetModelUsageDailyApiModelsModelIdUsageDailyGetParams,
) => {
  return [
    `/api/models/${modelId}/usage/daily`,
    ...(params ? [params] : []),
  ] as const;
};

export const getGetModelUsageDailyApiModelsModelIdUsageDailyGetQueryOptions = <
  TData = Awaited<
    ReturnType<typeof getModelUsageDailyApiModelsModelIdUsageDailyGet>
  >,
  TError = ErrorType<HTTPValidationError>,
>(
  modelId: string,
  params?: GetModelUsageDailyApiModelsModelIdUsageDailyGetParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<typeof getModelUsageDailyApiModelsModelIdUsageDailyGet>
        >,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof orvalFetch>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getGetModelUsageDailyApiModelsModelIdUsageDailyGetQueryKey(modelId, params);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getModelUsageDailyApiModelsModelIdUsageDailyGet>>
  > = ({ signal }) =>
    getModelUsageDailyApiModelsModelIdUsageDailyGet(modelId, params, {
      signal,
      ...requestOptions,
    });

  return {
    queryKey,
    queryFn,
    enabled: modelId !== null && modelId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<
    Awaited<ReturnType<typeof getModelUsageDailyApiModelsModelIdUsageDailyGet>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetModelUsageDailyApiModelsModelIdUsageDailyGetQueryResult =
  NonNullable<
    Awaited<ReturnType<typeof getModelUsageDailyApiModelsModelIdUsageDailyGet>>
  >;
export type GetModelUsageDailyApiModelsModelIdUsageDailyGetQueryError =
  ErrorType<HTTPValidationError>;

export function useGetModelUsageDailyApiModelsModelIdUsageDailyGet<
  TData = Awaited<
    ReturnType<typeof getModelUsageDailyApiModelsModelIdUsageDailyGet>
  >,
  TError = ErrorType<HTTPValidationError>,
>(
  modelId: string,
  params: undefined | GetModelUsageDailyApiModelsModelIdUsageDailyGetParams,
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<typeof getModelUsageDailyApiModelsModelIdUsageDailyGet>
        >,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<
            ReturnType<typeof getModelUsageDailyApiModelsModelIdUsageDailyGet>
          >,
          TError,
          Awaited<
            ReturnType<typeof getModelUsageDailyApiModelsModelIdUsageDailyGet>
          >
        >,
        "initialData"
      >;
    request?: SecondParameter<typeof orvalFetch>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetModelUsageDailyApiModelsModelIdUsageDailyGet<
  TData = Awaited<
    ReturnType<typeof getModelUsageDailyApiModelsModelIdUsageDailyGet>
  >,
  TError = ErrorType<HTTPValidationError>,
>(
  modelId: string,
  params?: GetModelUsageDailyApiModelsModelIdUsageDailyGetParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<typeof getModelUsageDailyApiModelsModelIdUsageDailyGet>
        >,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<
            ReturnType<typeof getModelUsageDailyApiModelsModelIdUsageDailyGet>
          >,
          TError,
          Awaited<
            ReturnType<typeof getModelUsageDailyApiModelsModelIdUsageDailyGet>
          >
        >,
        "initialData"
      >;
    request?: SecondParameter<typeof orvalFetch>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetModelUsageDailyApiModelsModelIdUsageDailyGet<
  TData = Awaited<
    ReturnType<typeof getModelUsageDailyApiModelsModelIdUsageDailyGet>
  >,
  TError = ErrorType<HTTPValidationError>,
>(
  modelId: string,
  params?: GetModelUsageDailyApiModelsModelIdUsageDailyGetParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<typeof getModelUsageDailyApiModelsModelIdUsageDailyGet>
        >,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof orvalFetch>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
/**
 * @summary Get Model Usage Daily
 */

export function useGetModelUsageDailyApiModelsModelIdUsageDailyGet<
  TData = Awaited<
    ReturnType<typeof getModelUsageDailyApiModelsModelIdUsageDailyGet>
  >,
  TError = ErrorType<HTTPValidationError>,
>(
  modelId: string,
  params?: GetModelUsageDailyApiModelsModelIdUsageDailyGetParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<typeof getModelUsageDailyApiModelsModelIdUsageDailyGet>
        >,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof orvalFetch>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions =
    getGetModelUsageDailyApiModelsModelIdUsageDailyGetQueryOptions(
      modelId,
      params,
      options,
    );

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}

export type getModelUsageTimeseriesApiModelsModelIdUsageTimeseriesGetResponse200 =
  {
    data: UsageTimeseriesResponse;
    status: 200;
  };

export type getModelUsageTimeseriesApiModelsModelIdUsageTimeseriesGetResponse422 =
  {
    data: HTTPValidationError;
    status: 422;
  };

export type getModelUsageTimeseriesApiModelsModelIdUsageTimeseriesGetResponseSuccess =
  getModelUsageTimeseriesApiModelsModelIdUsageTimeseriesGetResponse200 & {
    headers: Headers;
  };
export type getModelUsageTimeseriesApiModelsModelIdUsageTimeseriesGetResponseError =
  getModelUsageTimeseriesApiModelsModelIdUsageTimeseriesGetResponse422 & {
    headers: Headers;
  };

export type getModelUsageTimeseriesApiModelsModelIdUsageTimeseriesGetResponse =
  | getModelUsageTimeseriesApiModelsModelIdUsageTimeseriesGetResponseSuccess
  | getModelUsageTimeseriesApiModelsModelIdUsageTimeseriesGetResponseError;

export const getGetModelUsageTimeseriesApiModelsModelIdUsageTimeseriesGetUrl = (
  modelId: string,
  params?: GetModelUsageTimeseriesApiModelsModelIdUsageTimeseriesGetParams,
) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {
    if (value !== undefined) {
      normalizedParams.append(key, value === null ? "null" : String(value));
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0
    ? `/api/models/${modelId}/usage/timeseries?${stringifiedParams}`
    : `/api/models/${modelId}/usage/timeseries`;
};

/**
 * Bucketed usage for one model — the requests band's matrix and tiles.
 *
 * The gateway zero-fills the grid, so the series is contiguous and can be
 * indexed positionally. The window is floored onto the bucket grid, which
 * keeps the first and last buckets whole.
 * @summary Get Model Usage Timeseries
 */
export const getModelUsageTimeseriesApiModelsModelIdUsageTimeseriesGet = async (
  modelId: string,
  params?: GetModelUsageTimeseriesApiModelsModelIdUsageTimeseriesGetParams,
  options?: Parameters<typeof orvalFetch>[1],
): Promise<getModelUsageTimeseriesApiModelsModelIdUsageTimeseriesGetResponse> => {
  return orvalFetch<getModelUsageTimeseriesApiModelsModelIdUsageTimeseriesGetResponse>(
    getGetModelUsageTimeseriesApiModelsModelIdUsageTimeseriesGetUrl(
      modelId,
      params,
    ),
    {
      ...options,
      method: "GET",
    },
  );
};

export const getGetModelUsageTimeseriesApiModelsModelIdUsageTimeseriesGetQueryKey =
  (
    modelId: string,
    params?: GetModelUsageTimeseriesApiModelsModelIdUsageTimeseriesGetParams,
  ) => {
    return [
      `/api/models/${modelId}/usage/timeseries`,
      ...(params ? [params] : []),
    ] as const;
  };

export const getGetModelUsageTimeseriesApiModelsModelIdUsageTimeseriesGetQueryOptions =
  <
    TData = Awaited<
      ReturnType<
        typeof getModelUsageTimeseriesApiModelsModelIdUsageTimeseriesGet
      >
    >,
    TError = ErrorType<HTTPValidationError>,
  >(
    modelId: string,
    params?: GetModelUsageTimeseriesApiModelsModelIdUsageTimeseriesGetParams,
    options?: {
      query?: Partial<
        UseQueryOptions<
          Awaited<
            ReturnType<
              typeof getModelUsageTimeseriesApiModelsModelIdUsageTimeseriesGet
            >
          >,
          TError,
          TData
        >
      >;
      request?: SecondParameter<typeof orvalFetch>;
    },
  ) => {
    const { query: queryOptions, request: requestOptions } = options ?? {};

    const queryKey =
      queryOptions?.queryKey ??
      getGetModelUsageTimeseriesApiModelsModelIdUsageTimeseriesGetQueryKey(
        modelId,
        params,
      );

    const queryFn: QueryFunction<
      Awaited<
        ReturnType<
          typeof getModelUsageTimeseriesApiModelsModelIdUsageTimeseriesGet
        >
      >
    > = ({ signal }) =>
      getModelUsageTimeseriesApiModelsModelIdUsageTimeseriesGet(
        modelId,
        params,
        { signal, ...requestOptions },
      );

    return {
      queryKey,
      queryFn,
      enabled: modelId !== null && modelId !== undefined,
      ...queryOptions,
    } as UseQueryOptions<
      Awaited<
        ReturnType<
          typeof getModelUsageTimeseriesApiModelsModelIdUsageTimeseriesGet
        >
      >,
      TError,
      TData
    > & { queryKey: DataTag<QueryKey, TData, TError> };
  };

export type GetModelUsageTimeseriesApiModelsModelIdUsageTimeseriesGetQueryResult =
  NonNullable<
    Awaited<
      ReturnType<
        typeof getModelUsageTimeseriesApiModelsModelIdUsageTimeseriesGet
      >
    >
  >;
export type GetModelUsageTimeseriesApiModelsModelIdUsageTimeseriesGetQueryError =
  ErrorType<HTTPValidationError>;

export function useGetModelUsageTimeseriesApiModelsModelIdUsageTimeseriesGet<
  TData = Awaited<
    ReturnType<typeof getModelUsageTimeseriesApiModelsModelIdUsageTimeseriesGet>
  >,
  TError = ErrorType<HTTPValidationError>,
>(
  modelId: string,
  params:
    | undefined
    | GetModelUsageTimeseriesApiModelsModelIdUsageTimeseriesGetParams,
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<
            typeof getModelUsageTimeseriesApiModelsModelIdUsageTimeseriesGet
          >
        >,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<
            ReturnType<
              typeof getModelUsageTimeseriesApiModelsModelIdUsageTimeseriesGet
            >
          >,
          TError,
          Awaited<
            ReturnType<
              typeof getModelUsageTimeseriesApiModelsModelIdUsageTimeseriesGet
            >
          >
        >,
        "initialData"
      >;
    request?: SecondParameter<typeof orvalFetch>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetModelUsageTimeseriesApiModelsModelIdUsageTimeseriesGet<
  TData = Awaited<
    ReturnType<typeof getModelUsageTimeseriesApiModelsModelIdUsageTimeseriesGet>
  >,
  TError = ErrorType<HTTPValidationError>,
>(
  modelId: string,
  params?: GetModelUsageTimeseriesApiModelsModelIdUsageTimeseriesGetParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<
            typeof getModelUsageTimeseriesApiModelsModelIdUsageTimeseriesGet
          >
        >,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<
            ReturnType<
              typeof getModelUsageTimeseriesApiModelsModelIdUsageTimeseriesGet
            >
          >,
          TError,
          Awaited<
            ReturnType<
              typeof getModelUsageTimeseriesApiModelsModelIdUsageTimeseriesGet
            >
          >
        >,
        "initialData"
      >;
    request?: SecondParameter<typeof orvalFetch>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetModelUsageTimeseriesApiModelsModelIdUsageTimeseriesGet<
  TData = Awaited<
    ReturnType<typeof getModelUsageTimeseriesApiModelsModelIdUsageTimeseriesGet>
  >,
  TError = ErrorType<HTTPValidationError>,
>(
  modelId: string,
  params?: GetModelUsageTimeseriesApiModelsModelIdUsageTimeseriesGetParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<
            typeof getModelUsageTimeseriesApiModelsModelIdUsageTimeseriesGet
          >
        >,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof orvalFetch>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
/**
 * @summary Get Model Usage Timeseries
 */

export function useGetModelUsageTimeseriesApiModelsModelIdUsageTimeseriesGet<
  TData = Awaited<
    ReturnType<typeof getModelUsageTimeseriesApiModelsModelIdUsageTimeseriesGet>
  >,
  TError = ErrorType<HTTPValidationError>,
>(
  modelId: string,
  params?: GetModelUsageTimeseriesApiModelsModelIdUsageTimeseriesGetParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<
            typeof getModelUsageTimeseriesApiModelsModelIdUsageTimeseriesGet
          >
        >,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof orvalFetch>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions =
    getGetModelUsageTimeseriesApiModelsModelIdUsageTimeseriesGetQueryOptions(
      modelId,
      params,
      options,
    );

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}

export type getModelMetricsApiModelsModelIdMetricsGetResponse200 = {
  data: ModelMetricsResponse;
  status: 200;
};

export type getModelMetricsApiModelsModelIdMetricsGetResponse422 = {
  data: HTTPValidationError;
  status: 422;
};

export type getModelMetricsApiModelsModelIdMetricsGetResponseSuccess =
  getModelMetricsApiModelsModelIdMetricsGetResponse200 & {
    headers: Headers;
  };
export type getModelMetricsApiModelsModelIdMetricsGetResponseError =
  getModelMetricsApiModelsModelIdMetricsGetResponse422 & {
    headers: Headers;
  };

export type getModelMetricsApiModelsModelIdMetricsGetResponse =
  | getModelMetricsApiModelsModelIdMetricsGetResponseSuccess
  | getModelMetricsApiModelsModelIdMetricsGetResponseError;

export const getGetModelMetricsApiModelsModelIdMetricsGetUrl = (
  modelId: string,
  params?: GetModelMetricsApiModelsModelIdMetricsGetParams,
) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {
    if (value !== undefined) {
      normalizedParams.append(key, value === null ? "null" : String(value));
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0
    ? `/api/models/${modelId}/metrics?${stringifiedParams}`
    : `/api/models/${modelId}/metrics`;
};

/**
 * The model metrics page (TRA-1461): one window, bucketed by its length.
 *
 * Requests by status class, latency and time-to-first-token percentiles,
 * tokens and generation rate per bucket, plus the window's most frequent
 * error messages. The team comes from the session; the model must be the
 * team's own or this 404s like every other model read.
 *
 * `end` moves the window back in time (TRA-1592): the page's arrows read
 * only the stretch shown, at the bucket the same `hours` gets at now.
 * @summary Get Model Metrics
 */
export const getModelMetricsApiModelsModelIdMetricsGet = async (
  modelId: string,
  params?: GetModelMetricsApiModelsModelIdMetricsGetParams,
  options?: Parameters<typeof orvalFetch>[1],
): Promise<getModelMetricsApiModelsModelIdMetricsGetResponse> => {
  return orvalFetch<getModelMetricsApiModelsModelIdMetricsGetResponse>(
    getGetModelMetricsApiModelsModelIdMetricsGetUrl(modelId, params),
    {
      ...options,
      method: "GET",
    },
  );
};

export const getGetModelMetricsApiModelsModelIdMetricsGetQueryKey = (
  modelId: string,
  params?: GetModelMetricsApiModelsModelIdMetricsGetParams,
) => {
  return [
    `/api/models/${modelId}/metrics`,
    ...(params ? [params] : []),
  ] as const;
};

export const getGetModelMetricsApiModelsModelIdMetricsGetQueryOptions = <
  TData = Awaited<ReturnType<typeof getModelMetricsApiModelsModelIdMetricsGet>>,
  TError = ErrorType<HTTPValidationError>,
>(
  modelId: string,
  params?: GetModelMetricsApiModelsModelIdMetricsGetParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getModelMetricsApiModelsModelIdMetricsGet>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof orvalFetch>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getGetModelMetricsApiModelsModelIdMetricsGetQueryKey(modelId, params);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getModelMetricsApiModelsModelIdMetricsGet>>
  > = ({ signal }) =>
    getModelMetricsApiModelsModelIdMetricsGet(modelId, params, {
      signal,
      ...requestOptions,
    });

  return {
    queryKey,
    queryFn,
    enabled: modelId !== null && modelId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<
    Awaited<ReturnType<typeof getModelMetricsApiModelsModelIdMetricsGet>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetModelMetricsApiModelsModelIdMetricsGetQueryResult = NonNullable<
  Awaited<ReturnType<typeof getModelMetricsApiModelsModelIdMetricsGet>>
>;
export type GetModelMetricsApiModelsModelIdMetricsGetQueryError =
  ErrorType<HTTPValidationError>;

export function useGetModelMetricsApiModelsModelIdMetricsGet<
  TData = Awaited<ReturnType<typeof getModelMetricsApiModelsModelIdMetricsGet>>,
  TError = ErrorType<HTTPValidationError>,
>(
  modelId: string,
  params: undefined | GetModelMetricsApiModelsModelIdMetricsGetParams,
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getModelMetricsApiModelsModelIdMetricsGet>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getModelMetricsApiModelsModelIdMetricsGet>>,
          TError,
          Awaited<ReturnType<typeof getModelMetricsApiModelsModelIdMetricsGet>>
        >,
        "initialData"
      >;
    request?: SecondParameter<typeof orvalFetch>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetModelMetricsApiModelsModelIdMetricsGet<
  TData = Awaited<ReturnType<typeof getModelMetricsApiModelsModelIdMetricsGet>>,
  TError = ErrorType<HTTPValidationError>,
>(
  modelId: string,
  params?: GetModelMetricsApiModelsModelIdMetricsGetParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getModelMetricsApiModelsModelIdMetricsGet>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getModelMetricsApiModelsModelIdMetricsGet>>,
          TError,
          Awaited<ReturnType<typeof getModelMetricsApiModelsModelIdMetricsGet>>
        >,
        "initialData"
      >;
    request?: SecondParameter<typeof orvalFetch>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetModelMetricsApiModelsModelIdMetricsGet<
  TData = Awaited<ReturnType<typeof getModelMetricsApiModelsModelIdMetricsGet>>,
  TError = ErrorType<HTTPValidationError>,
>(
  modelId: string,
  params?: GetModelMetricsApiModelsModelIdMetricsGetParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getModelMetricsApiModelsModelIdMetricsGet>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof orvalFetch>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
/**
 * @summary Get Model Metrics
 */

export function useGetModelMetricsApiModelsModelIdMetricsGet<
  TData = Awaited<ReturnType<typeof getModelMetricsApiModelsModelIdMetricsGet>>,
  TError = ErrorType<HTTPValidationError>,
>(
  modelId: string,
  params?: GetModelMetricsApiModelsModelIdMetricsGetParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getModelMetricsApiModelsModelIdMetricsGet>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof orvalFetch>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions = getGetModelMetricsApiModelsModelIdMetricsGetQueryOptions(
    modelId,
    params,
    options,
  );

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}

export type getModelUptimeApiModelsModelIdUptimeGetResponse200 = {
  data: UptimeResponse;
  status: 200;
};

export type getModelUptimeApiModelsModelIdUptimeGetResponse422 = {
  data: HTTPValidationError;
  status: 422;
};

export type getModelUptimeApiModelsModelIdUptimeGetResponseSuccess =
  getModelUptimeApiModelsModelIdUptimeGetResponse200 & {
    headers: Headers;
  };
export type getModelUptimeApiModelsModelIdUptimeGetResponseError =
  getModelUptimeApiModelsModelIdUptimeGetResponse422 & {
    headers: Headers;
  };

export type getModelUptimeApiModelsModelIdUptimeGetResponse =
  | getModelUptimeApiModelsModelIdUptimeGetResponseSuccess
  | getModelUptimeApiModelsModelIdUptimeGetResponseError;

export const getGetModelUptimeApiModelsModelIdUptimeGetUrl = (
  modelId: string,
  params?: GetModelUptimeApiModelsModelIdUptimeGetParams,
) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {
    if (value !== undefined) {
      normalizedParams.append(key, value === null ? "null" : String(value));
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0
    ? `/api/models/${modelId}/uptime?${stringifiedParams}`
    : `/api/models/${modelId}/uptime`;
};

/**
 * Ninety days of eight-hour availability blocks for one model.
 * @summary Get Model Uptime
 */
export const getModelUptimeApiModelsModelIdUptimeGet = async (
  modelId: string,
  params?: GetModelUptimeApiModelsModelIdUptimeGetParams,
  options?: Parameters<typeof orvalFetch>[1],
): Promise<getModelUptimeApiModelsModelIdUptimeGetResponse> => {
  return orvalFetch<getModelUptimeApiModelsModelIdUptimeGetResponse>(
    getGetModelUptimeApiModelsModelIdUptimeGetUrl(modelId, params),
    {
      ...options,
      method: "GET",
    },
  );
};

export const getGetModelUptimeApiModelsModelIdUptimeGetQueryKey = (
  modelId: string,
  params?: GetModelUptimeApiModelsModelIdUptimeGetParams,
) => {
  return [
    `/api/models/${modelId}/uptime`,
    ...(params ? [params] : []),
  ] as const;
};

export const getGetModelUptimeApiModelsModelIdUptimeGetQueryOptions = <
  TData = Awaited<ReturnType<typeof getModelUptimeApiModelsModelIdUptimeGet>>,
  TError = ErrorType<HTTPValidationError>,
>(
  modelId: string,
  params?: GetModelUptimeApiModelsModelIdUptimeGetParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getModelUptimeApiModelsModelIdUptimeGet>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof orvalFetch>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getGetModelUptimeApiModelsModelIdUptimeGetQueryKey(modelId, params);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getModelUptimeApiModelsModelIdUptimeGet>>
  > = ({ signal }) =>
    getModelUptimeApiModelsModelIdUptimeGet(modelId, params, {
      signal,
      ...requestOptions,
    });

  return {
    queryKey,
    queryFn,
    enabled: modelId !== null && modelId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<
    Awaited<ReturnType<typeof getModelUptimeApiModelsModelIdUptimeGet>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetModelUptimeApiModelsModelIdUptimeGetQueryResult = NonNullable<
  Awaited<ReturnType<typeof getModelUptimeApiModelsModelIdUptimeGet>>
>;
export type GetModelUptimeApiModelsModelIdUptimeGetQueryError =
  ErrorType<HTTPValidationError>;

export function useGetModelUptimeApiModelsModelIdUptimeGet<
  TData = Awaited<ReturnType<typeof getModelUptimeApiModelsModelIdUptimeGet>>,
  TError = ErrorType<HTTPValidationError>,
>(
  modelId: string,
  params: undefined | GetModelUptimeApiModelsModelIdUptimeGetParams,
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getModelUptimeApiModelsModelIdUptimeGet>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getModelUptimeApiModelsModelIdUptimeGet>>,
          TError,
          Awaited<ReturnType<typeof getModelUptimeApiModelsModelIdUptimeGet>>
        >,
        "initialData"
      >;
    request?: SecondParameter<typeof orvalFetch>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetModelUptimeApiModelsModelIdUptimeGet<
  TData = Awaited<ReturnType<typeof getModelUptimeApiModelsModelIdUptimeGet>>,
  TError = ErrorType<HTTPValidationError>,
>(
  modelId: string,
  params?: GetModelUptimeApiModelsModelIdUptimeGetParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getModelUptimeApiModelsModelIdUptimeGet>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getModelUptimeApiModelsModelIdUptimeGet>>,
          TError,
          Awaited<ReturnType<typeof getModelUptimeApiModelsModelIdUptimeGet>>
        >,
        "initialData"
      >;
    request?: SecondParameter<typeof orvalFetch>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetModelUptimeApiModelsModelIdUptimeGet<
  TData = Awaited<ReturnType<typeof getModelUptimeApiModelsModelIdUptimeGet>>,
  TError = ErrorType<HTTPValidationError>,
>(
  modelId: string,
  params?: GetModelUptimeApiModelsModelIdUptimeGetParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getModelUptimeApiModelsModelIdUptimeGet>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof orvalFetch>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
/**
 * @summary Get Model Uptime
 */

export function useGetModelUptimeApiModelsModelIdUptimeGet<
  TData = Awaited<ReturnType<typeof getModelUptimeApiModelsModelIdUptimeGet>>,
  TError = ErrorType<HTTPValidationError>,
>(
  modelId: string,
  params?: GetModelUptimeApiModelsModelIdUptimeGetParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getModelUptimeApiModelsModelIdUptimeGet>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof orvalFetch>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions = getGetModelUptimeApiModelsModelIdUptimeGetQueryOptions(
    modelId,
    params,
    options,
  );

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}

export type getModelCheckpointsApiModelsModelIdCheckpointsGetResponse200 = {
  data: CheckpointsResponse;
  status: 200;
};

export type getModelCheckpointsApiModelsModelIdCheckpointsGetResponse422 = {
  data: HTTPValidationError;
  status: 422;
};

export type getModelCheckpointsApiModelsModelIdCheckpointsGetResponseSuccess =
  getModelCheckpointsApiModelsModelIdCheckpointsGetResponse200 & {
    headers: Headers;
  };
export type getModelCheckpointsApiModelsModelIdCheckpointsGetResponseError =
  getModelCheckpointsApiModelsModelIdCheckpointsGetResponse422 & {
    headers: Headers;
  };

export type getModelCheckpointsApiModelsModelIdCheckpointsGetResponse =
  | getModelCheckpointsApiModelsModelIdCheckpointsGetResponseSuccess
  | getModelCheckpointsApiModelsModelIdCheckpointsGetResponseError;

export const getGetModelCheckpointsApiModelsModelIdCheckpointsGetUrl = (
  modelId: string,
) => {
  return `/api/models/${modelId}/checkpoints`;
};

/**
 * The checkpoints this model was trained through, newest first.
 *
 * Derived from `eval_runs.model_slug` — there is no checkpoint table.
 * Every row is scored on ONE eval group (see `services/checkpoints.py`)
 * so the column is comparable and the training band can draw it as a
 * curve. A model with no eval runs returns a null group and no rows.
 * @summary Get Model Checkpoints
 */
export const getModelCheckpointsApiModelsModelIdCheckpointsGet = async (
  modelId: string,
  options?: Parameters<typeof orvalFetch>[1],
): Promise<getModelCheckpointsApiModelsModelIdCheckpointsGetResponse> => {
  return orvalFetch<getModelCheckpointsApiModelsModelIdCheckpointsGetResponse>(
    getGetModelCheckpointsApiModelsModelIdCheckpointsGetUrl(modelId),
    {
      ...options,
      method: "GET",
    },
  );
};

export const getGetModelCheckpointsApiModelsModelIdCheckpointsGetQueryKey = (
  modelId: string,
) => {
  return [`/api/models/${modelId}/checkpoints`] as const;
};

export const getGetModelCheckpointsApiModelsModelIdCheckpointsGetQueryOptions =
  <
    TData = Awaited<
      ReturnType<typeof getModelCheckpointsApiModelsModelIdCheckpointsGet>
    >,
    TError = ErrorType<HTTPValidationError>,
  >(
    modelId: string,
    options?: {
      query?: Partial<
        UseQueryOptions<
          Awaited<
            ReturnType<typeof getModelCheckpointsApiModelsModelIdCheckpointsGet>
          >,
          TError,
          TData
        >
      >;
      request?: SecondParameter<typeof orvalFetch>;
    },
  ) => {
    const { query: queryOptions, request: requestOptions } = options ?? {};

    const queryKey =
      queryOptions?.queryKey ??
      getGetModelCheckpointsApiModelsModelIdCheckpointsGetQueryKey(modelId);

    const queryFn: QueryFunction<
      Awaited<
        ReturnType<typeof getModelCheckpointsApiModelsModelIdCheckpointsGet>
      >
    > = ({ signal }) =>
      getModelCheckpointsApiModelsModelIdCheckpointsGet(modelId, {
        signal,
        ...requestOptions,
      });

    return {
      queryKey,
      queryFn,
      enabled: modelId !== null && modelId !== undefined,
      ...queryOptions,
    } as UseQueryOptions<
      Awaited<
        ReturnType<typeof getModelCheckpointsApiModelsModelIdCheckpointsGet>
      >,
      TError,
      TData
    > & { queryKey: DataTag<QueryKey, TData, TError> };
  };

export type GetModelCheckpointsApiModelsModelIdCheckpointsGetQueryResult =
  NonNullable<
    Awaited<
      ReturnType<typeof getModelCheckpointsApiModelsModelIdCheckpointsGet>
    >
  >;
export type GetModelCheckpointsApiModelsModelIdCheckpointsGetQueryError =
  ErrorType<HTTPValidationError>;

export function useGetModelCheckpointsApiModelsModelIdCheckpointsGet<
  TData = Awaited<
    ReturnType<typeof getModelCheckpointsApiModelsModelIdCheckpointsGet>
  >,
  TError = ErrorType<HTTPValidationError>,
>(
  modelId: string,
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<typeof getModelCheckpointsApiModelsModelIdCheckpointsGet>
        >,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<
            ReturnType<typeof getModelCheckpointsApiModelsModelIdCheckpointsGet>
          >,
          TError,
          Awaited<
            ReturnType<typeof getModelCheckpointsApiModelsModelIdCheckpointsGet>
          >
        >,
        "initialData"
      >;
    request?: SecondParameter<typeof orvalFetch>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetModelCheckpointsApiModelsModelIdCheckpointsGet<
  TData = Awaited<
    ReturnType<typeof getModelCheckpointsApiModelsModelIdCheckpointsGet>
  >,
  TError = ErrorType<HTTPValidationError>,
>(
  modelId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<typeof getModelCheckpointsApiModelsModelIdCheckpointsGet>
        >,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<
            ReturnType<typeof getModelCheckpointsApiModelsModelIdCheckpointsGet>
          >,
          TError,
          Awaited<
            ReturnType<typeof getModelCheckpointsApiModelsModelIdCheckpointsGet>
          >
        >,
        "initialData"
      >;
    request?: SecondParameter<typeof orvalFetch>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetModelCheckpointsApiModelsModelIdCheckpointsGet<
  TData = Awaited<
    ReturnType<typeof getModelCheckpointsApiModelsModelIdCheckpointsGet>
  >,
  TError = ErrorType<HTTPValidationError>,
>(
  modelId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<typeof getModelCheckpointsApiModelsModelIdCheckpointsGet>
        >,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof orvalFetch>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
/**
 * @summary Get Model Checkpoints
 */

export function useGetModelCheckpointsApiModelsModelIdCheckpointsGet<
  TData = Awaited<
    ReturnType<typeof getModelCheckpointsApiModelsModelIdCheckpointsGet>
  >,
  TError = ErrorType<HTTPValidationError>,
>(
  modelId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<typeof getModelCheckpointsApiModelsModelIdCheckpointsGet>
        >,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof orvalFetch>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions =
    getGetModelCheckpointsApiModelsModelIdCheckpointsGetQueryOptions(
      modelId,
      options,
    );

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}

export type getModelActivityApiModelsModelIdActivityGetResponse200 = {
  data: ModelActivityResponse;
  status: 200;
};

export type getModelActivityApiModelsModelIdActivityGetResponse422 = {
  data: HTTPValidationError;
  status: 422;
};

export type getModelActivityApiModelsModelIdActivityGetResponseSuccess =
  getModelActivityApiModelsModelIdActivityGetResponse200 & {
    headers: Headers;
  };
export type getModelActivityApiModelsModelIdActivityGetResponseError =
  getModelActivityApiModelsModelIdActivityGetResponse422 & {
    headers: Headers;
  };

export type getModelActivityApiModelsModelIdActivityGetResponse =
  | getModelActivityApiModelsModelIdActivityGetResponseSuccess
  | getModelActivityApiModelsModelIdActivityGetResponseError;

export const getGetModelActivityApiModelsModelIdActivityGetUrl = (
  modelId: string,
) => {
  return `/api/models/${modelId}/activity`;
};

/**
 * This model's lifecycle feed, newest first.
 *
 * Composed from three sources we already hold rather than an event table:
 * when the model was created (platform DB), when each checkpoint was
 * scored and which one is serving (eval runs), and when the gateway's
 * probes saw it unavailable (D1). An outage that spans several blocks is
 * ONE event — the wall already renders block granularity, and a feed that
 * repeats "down" every eight hours buries everything else.
 * @summary Get Model Activity
 */
export const getModelActivityApiModelsModelIdActivityGet = async (
  modelId: string,
  options?: Parameters<typeof orvalFetch>[1],
): Promise<getModelActivityApiModelsModelIdActivityGetResponse> => {
  return orvalFetch<getModelActivityApiModelsModelIdActivityGetResponse>(
    getGetModelActivityApiModelsModelIdActivityGetUrl(modelId),
    {
      ...options,
      method: "GET",
    },
  );
};

export const getGetModelActivityApiModelsModelIdActivityGetQueryKey = (
  modelId: string,
) => {
  return [`/api/models/${modelId}/activity`] as const;
};

export const getGetModelActivityApiModelsModelIdActivityGetQueryOptions = <
  TData = Awaited<
    ReturnType<typeof getModelActivityApiModelsModelIdActivityGet>
  >,
  TError = ErrorType<HTTPValidationError>,
>(
  modelId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getModelActivityApiModelsModelIdActivityGet>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof orvalFetch>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getGetModelActivityApiModelsModelIdActivityGetQueryKey(modelId);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getModelActivityApiModelsModelIdActivityGet>>
  > = ({ signal }) =>
    getModelActivityApiModelsModelIdActivityGet(modelId, {
      signal,
      ...requestOptions,
    });

  return {
    queryKey,
    queryFn,
    enabled: modelId !== null && modelId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<
    Awaited<ReturnType<typeof getModelActivityApiModelsModelIdActivityGet>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetModelActivityApiModelsModelIdActivityGetQueryResult =
  NonNullable<
    Awaited<ReturnType<typeof getModelActivityApiModelsModelIdActivityGet>>
  >;
export type GetModelActivityApiModelsModelIdActivityGetQueryError =
  ErrorType<HTTPValidationError>;

export function useGetModelActivityApiModelsModelIdActivityGet<
  TData = Awaited<
    ReturnType<typeof getModelActivityApiModelsModelIdActivityGet>
  >,
  TError = ErrorType<HTTPValidationError>,
>(
  modelId: string,
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getModelActivityApiModelsModelIdActivityGet>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<
            ReturnType<typeof getModelActivityApiModelsModelIdActivityGet>
          >,
          TError,
          Awaited<
            ReturnType<typeof getModelActivityApiModelsModelIdActivityGet>
          >
        >,
        "initialData"
      >;
    request?: SecondParameter<typeof orvalFetch>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetModelActivityApiModelsModelIdActivityGet<
  TData = Awaited<
    ReturnType<typeof getModelActivityApiModelsModelIdActivityGet>
  >,
  TError = ErrorType<HTTPValidationError>,
>(
  modelId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getModelActivityApiModelsModelIdActivityGet>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<
            ReturnType<typeof getModelActivityApiModelsModelIdActivityGet>
          >,
          TError,
          Awaited<
            ReturnType<typeof getModelActivityApiModelsModelIdActivityGet>
          >
        >,
        "initialData"
      >;
    request?: SecondParameter<typeof orvalFetch>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetModelActivityApiModelsModelIdActivityGet<
  TData = Awaited<
    ReturnType<typeof getModelActivityApiModelsModelIdActivityGet>
  >,
  TError = ErrorType<HTTPValidationError>,
>(
  modelId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getModelActivityApiModelsModelIdActivityGet>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof orvalFetch>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
/**
 * @summary Get Model Activity
 */

export function useGetModelActivityApiModelsModelIdActivityGet<
  TData = Awaited<
    ReturnType<typeof getModelActivityApiModelsModelIdActivityGet>
  >,
  TError = ErrorType<HTTPValidationError>,
>(
  modelId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getModelActivityApiModelsModelIdActivityGet>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof orvalFetch>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions =
    getGetModelActivityApiModelsModelIdActivityGetQueryOptions(
      modelId,
      options,
    );

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}
